import { canHoldStoredCall } from '../call-management/holdPolicy';
import { appendCallHistoryCache, currentCallHistoryScope } from '../mobile/navigation/callHistoryStorage';
/**
 * Middleware for the interpreter queue feature.
 *
 * Connects the InterpreterQueueService WebSocket events to the Redux store,
 * enabling real-time updates for interpreter matching and queue status.
 */
import { appNavigate } from '../app/actions';
import { hangup } from '../base/connection/actions';
import { twilioVoiceService } from '../interpreter-phone/services/TwilioVoiceService';
import { storedTransferredPhoneLeg } from '../interpreter-phone/transferredPhoneLeg';
import { CONNECTION_ESTABLISHED, CONNECTION_FAILED } from '../base/connection/actionTypes';
import { DATA_CHANNEL_OPENED } from '../base/conference/actionTypes';
import { TRACK_ADDED, TRACK_UPDATED } from '../base/tracks/actionTypes';
import { PARTICIPANT_JOINED } from '../base/participants/actionTypes';
import { CONFERENCE_FAILED, CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_LEAVE, SET_ROOM, VRI_SESSION_END_REQUESTED } from '../base/conference/actionTypes';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { getUserRole, isPlatformCallRoom } from '../base/user-role/functions';
import { getBackendSafeRoomName } from '../base/util/uri';
import { getAppName, isVriApp } from '../base/whitelabel/functions';
import { updateSettings } from '../base/settings/actions';
import { APP_STATE_CHANGED } from '../mobile/background/actionTypes';
import { SET_NETWORK_INFO } from '../base/net-info/actionTypes';
import { signOutMobileUser } from '../mobile/navigation/mobileLogout';
import { resetRoot } from '../mobile/navigation/rootNavigationContainerRef';
import { screen } from '../mobile/navigation/routes';
import { isVriCall } from '../mobile/navigation/serviceModes';
import { getPersistentItem, getPersistentJson, removePersistentItem, setPersistentItem } from '../vrs-auth/storage';
import { getSecureItem } from '../vrs-auth/secureStorage';
import { mobileLog } from '../mobile/navigation/logging';
import { emitMobileTelemetryEvent } from '../mobile/navigation/telemetry';
import { BUTTON_TYPES } from '../base/ui/constants.any';
import { hideNotification, showNotification } from '../notifications/actions';
import { FORCED_SIGN_OUT_NOTIFICATION_ID, NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { apiClient, onAuthSessionEnded } from '../shared/api-client';
import { noteCallEndedHere } from './activeCallGate';
import { MEDIA_REJOIN_RELOAD_SECONDS, isMediaRejoinPending, isMediaSessionFailure, mediaFailureMessage, readMediaRejoinMarker, recordMediaFailure, writeMediaRejoinMarker } from './inCallMediaRejoin';
import { recordP2PInterpreterLeft, recordP2PInterpreterOnCall } from './p2pInterpreterEligibility';
import { startRoomTabPresence, stopRoomTabPresence } from './roomTabPresence';
import { isRecord, stringValue } from './queueServiceUtils';
import { rememberUnansweredCall, storedOutgoingRing, withoutOutgoingRing } from './webOutgoingRing';
import { wrapUpCallbackOffer } from './wrapUp';
import { cardEndedByError, TRANSFER_PHONE_LEG_FAILED_TEXT, forgetTransferAction, lastTransferAction, transferCardId, transferOfferEndedText, transferOfferToRequest, transferTakingOverText } from './transferOffers';
import { teamCardId, teamJoinedMatch, teamOfferEndedText, teamOfferToRequest } from './teamOffers';
import { roomDisplayName } from './providerDisplayName';
import { beginInterpreterWrapUp, callBackRefused, dismissInterpreterWrapUp, forgetWrapUpQueueCount, noteWrapUpQueueStatus } from './wrapUpActions';
import { getCallPeerLabel, getPhoneCallLabel } from '../../../shared/call-display-name';
import { anchorAwayBanner, anchorEndNotice, callbackStatusView, interpreterLeftNotice } from '../../../shared/interpreted-anchor';
import { isRoomActivationHeld, queueService } from './InterpreterQueueService';
import { CANCEL_REQUEST, INTERPRETER_REQUEST_RECEIVED, MATCH_FOUND, MOVE_CALL_HERE, RESUME_HELD_CALL, SWITCH_TO_HELD_CALL, END_HELD_CALL, MERGE_HELD_CALL, QUEUE_SERVICE_ERROR, REQUEST_ACCEPTED, REQUEST_DECLINED, EXTERNAL_MEETING_CALL_ENDED, EXTERNAL_MEETING_CALL_UPDATED, MEETING_REMINDERS_ADDED, QUEUE_CAPABILITIES_UPDATED, CALL_END_BEFORE_NAVIGATION, INTERPRETER_MEETING_CALL_ENDED, INTERPRETER_MEETING_CALL_UPDATED, INTERPRETER_MEETING_STEP_REFUSED, MEETING_DESK_STATUS_CHANGED, START_CALL_AFTER_BUSY } from './actionTypes';
import { accountCallElsewhereChanged, interpreterAvailabilityChanged, interpreterBreakChanged, interpreterSessionSuperseded, interpreterRequestReceived, interpretedCallEnded, matchFound, meetingInitiated, queueConnectionChanged, queueStatusUpdate } from './actions';
import { conferenceErrorDetails, isConferenceForActiveRoom, shouldAutoEnterMatchedRoom, shouldPreserveMatchedCallAfterExit } from './callLifecycleGuards';
import { getRoomNavigationTarget } from './roomNavigation';
import { takeAbandonedAnswer } from './abandonedAnswers';
import { meetingCallFromAccept, meetingCallFromSnapshot, meetingDeskFromStatus, withMeetingAck } from './meetingRemote';
import { meetingReminderSchema } from '../shared/api-client/meeting-reminders';
import { getClientStartWithVideoMuted } from './mediaDefaults';
import { callOffHold, clearIncomingP2PCall, heldCallCleared, heldCallSet, incomingP2PCall } from '../call-management/actions';
import { heldCallFromRecord, takeCallWaitingAnswer } from '../call-management/callWaitingSwitch';
import { JOIN_TIMELINE_KEY, parseJoinMarker, serializeJoinMarker } from './joinTimeline';
import { describeP2PUnavailable } from '../mobile/navigation/p2pUnavailableCopy';
import { declineNotice, groupNoAnswerText, groupRingGaps, isOtherParticipantDeparture, isPlatformCallRoomName, participantNoticeTitle, rememberGroupRinging } from '../vrs-layout/groupInvite';
import { VRS_PEER_RECONNECTED, VRS_PEER_RECONNECTING } from '../vrs-layout/callStage';
import { profilePhotoPath, rememberCallPhoto, rememberedCallPhoto } from '../vrs-layout/profilePhoto';
import { isGroupRejoinPending, rememberGroupRejoin } from './groupRejoin';
import { NO_ANSWER_MESSAGE, cancelOutgoingRingOnExit, getOutgoingRingView, installOutgoingRing, isOutgoingRingCancelError, noteOutgoingRingAnswered, resetOutgoingRing, resumeOutgoingRing, subscribeOutgoingRing } from './outgoingRing';
import { installScheduledRequest, isScheduledRequestError, resetScheduledRequest } from './scheduledRequest';
import { endedForAllNotice, rememberCallEndNotice } from './groupEndForAll';
import { isObserverRoomPage } from '../vrs-auth/observerRoomPage';
import { moveNotice, watchHeldSession } from './accountDeviceNotices';
import { accountDevicePageActions, bindAccountDeviceDispatch, dismissAccountDeviceNotice, isAccountDeviceRoomPage, leaveConferenceWithoutEnding, presentAccountDeviceNotice } from './accountDevicePresenter';
let lastAutoEnteredQueueRoom;
/**
 * The platform (direct/group) call this browser last entered a room for.
 * Replayed join events for it are ignored, and a no-answer for it leaves
 * the room instead of parking the caller alone in it.
 */
let lastPlatformCall;
// Call-backs this app is held for (anchor_hold), by call id: who the room is
// waiting for, so the release can name the call (docs/interpreted-group-calls.md).
const heldCallbacks = new Map();
let callStartTimestamp;
let activeCallReconciliationInFlight = false;
let lastActiveCallReconciliationAt = 0;
let activeCallReconciliationTimer;
let conferenceExitInProgressKey;
// The VRI session this device's client ended from the room (End call): the
// server was told at once, and the room is closing itself.
let vriSessionEndSentFor;
let conferenceJoinedForActiveCall = false;
let joinedConferenceForActiveCall;
let conferenceJoinFailure;
let preJoinExitHandledKey;
let queueRoomNavigationStartedAt;
let queueRoomSignalingConnectedAt;
// The two marks after "joined" that tell a slow handshake apart from a slow
// signalling connect: media path up (data channel over the ICE/DTLS
// transport) and first remote video track. These locate a delay; they do
// not establish its cause without the corresponding connection evidence.
let queueRoomMediaConnectedAt;
let queueRoomRemoteVideoAt;
let queueRoomJoinSource;
let activeConferenceJoinedAt;
/**
 * A media-session failure of the joined call's conference, and whether it is
 * rejoined rather than ended (inCallMediaRejoin.ts). Recorded before the
 * reducer sees CONFERENCE_FAILED; the exit that follows reads it.
 */
let inCallMediaFailure;
/**
 * The room's latest camera change, reported with a failure: whether it came
 * right after a camera turned on is the Pixel incident's open question.
 */
let lastVideoChange;
/**
 * Report the first observed transport and connection/session transitions.
 * Multiple sessions can be an ordinary JVB/P2P transition, and transport
 * protocol alone does not establish the cause of a slow join.
 *
 * The media path is reported again when the call first runs over the other
 * kind of connection (a two-person call starts on the bridge and moves
 * direct), so the direct path's candidate types (host, srflx, or our TURN
 * relay) are known too: at most two reports per conference.
 *
 * Raw lib-jitsi-meet event names are used on purpose: importing the event
 * enums here would drag the whole SDK into every sandboxed harness that
 * loads this file.
 */
const LJM_LOCAL_STATS_UPDATED = 'cq.local_stats_updated';
const LJM_CONNECTION_INTERRUPTED = 'conference.connectionInterrupted';
const LJM_CONNECTION_RESTORED = 'conference.connectionRestored';
const LJM_CONNECTION_ESTABLISHED = 'conference.connectionEstablished';
const LJM_MEDIA_SESSION_STARTED = 'conference.media_session.started';
let mediaTransportObserver;
function observeMediaTransport(conference, options) {
    const observable = conference;
    mediaTransportObserver?.dispose();
    if (typeof observable.on !== 'function' || typeof observable.off !== 'function') {
        return;
    }
    const joinedAt = Date.now();
    let active = true;
    let reportedP2P;
    const onStats = (...args) => {
        if (!active) {
            return;
        }
        const stats = args[0];
        const raw = stats && typeof stats === 'object' && 'transport' in stats ? stats.transport : undefined;
        const candidateType = (value) => typeof value === 'string'
            && ['host', 'srflx', 'prflx', 'relay'].includes(value) ? value : undefined;
        const transports = (Array.isArray(raw) ? raw : []).slice(0, 3).flatMap((entry) => {
            if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
                return [];
            }
            const row = entry;
            const localCandidateType = candidateType(row.localCandidateType);
            const remoteCandidateType = candidateType(row.remoteCandidateType);
            const protocol = row.type === 'udp' || row.type === 'tcp' ? row.type : undefined;
            if (!localCandidateType && !remoteCandidateType && !protocol) {
                return [];
            }
            return [{
                    localCandidateType,
                    p2p: typeof row.p2p === 'boolean' ? row.p2p : undefined,
                    protocol,
                    relayed: localCandidateType === 'relay' || remoteCandidateType === 'relay',
                    remoteCandidateType,
                    rttMs: typeof row.rtt === 'number' && Number.isFinite(row.rtt) && row.rtt >= 0
                        ? Math.round(row.rtt) : undefined
                }];
        });
        if (!transports.length) {
            return;
        }
        const p2p = transports[0].p2p === true;
        if (reportedP2P === p2p) {
            return;
        }
        // The second report (the other kind of connection) is the last.
        if (reportedP2P !== undefined) {
            observable.off?.(LJM_LOCAL_STATS_UPDATED, onStats);
        }
        reportedP2P = p2p;
        emitMobileTelemetryEvent('vrs_media_transport', {
            sinceJoinMs: Math.max(0, Date.now() - joinedAt),
            // Candidate types and protocol only — never addresses.
            transports
        }, { ...options, state: p2p ? 'media_connected_p2p' : 'media_connected' });
    };
    let sessions = 0;
    const onSessionStarted = (...args) => {
        if (!active) {
            return;
        }
        const session = args[0];
        const p2p = session && typeof session === 'object' && 'isP2P' in session
            && typeof session.isP2P === 'boolean' ? session.isP2P : undefined;
        sessions += 1;
        emitMobileTelemetryEvent('vrs_media_session_started', {
            index: sessions,
            p2p,
            sinceJoinMs: Math.max(0, Date.now() - joinedAt)
        }, { ...options, state: 'started' });
    };
    const onEstablished = () => active && emitMobileTelemetryEvent('vrs_ice_connected', {
        sessions,
        sinceJoinMs: Math.max(0, Date.now() - joinedAt)
    }, { ...options, state: 'ice_connected' });
    // lib-jitsi-meet also says "restored" after every move between direct
    // and the bridge; only a restore that ends a reported drop is one.
    let interrupted = false;
    const onInterrupted = () => {
        if (!active) {
            return;
        }
        interrupted = true;
        emitMobileTelemetryEvent('vrs_ice_interrupted', {
            sinceJoinMs: Math.max(0, Date.now() - joinedAt)
        }, { ...options, state: 'interrupted' });
    };
    const onRestored = () => {
        if (!active || !interrupted) {
            return;
        }
        interrupted = false;
        emitMobileTelemetryEvent('vrs_ice_restored', {
            sinceJoinMs: Math.max(0, Date.now() - joinedAt)
        }, { ...options, state: 'restored' });
    };
    observable.on(LJM_LOCAL_STATS_UPDATED, onStats);
    observable.on(LJM_MEDIA_SESSION_STARTED, onSessionStarted);
    observable.on(LJM_CONNECTION_ESTABLISHED, onEstablished);
    observable.on(LJM_CONNECTION_INTERRUPTED, onInterrupted);
    observable.on(LJM_CONNECTION_RESTORED, onRestored);
    const observer = {
        conference,
        dispose: () => {
            active = false;
            observable.off?.(LJM_LOCAL_STATS_UPDATED, onStats);
            observable.off?.(LJM_MEDIA_SESSION_STARTED, onSessionStarted);
            observable.off?.(LJM_CONNECTION_ESTABLISHED, onEstablished);
            observable.off?.(LJM_CONNECTION_INTERRUPTED, onInterrupted);
            observable.off?.(LJM_CONNECTION_RESTORED, onRestored);
            if (mediaTransportObserver === observer) {
                mediaTransportObserver = undefined;
            }
        }
    };
    mediaTransportObserver = observer;
}
/**
 * On web the room is a full page load, so the navigation timestamp taken
 * above is gone by the time signaling connects. Whoever started the
 * navigation left a marker in localStorage; adopt it once for the room the
 * page is showing so the stage events measure from the tap, not from boot.
 */
function ensureJoinStartRestored() {
    if (queueRoomNavigationStartedAt || typeof window === 'undefined') {
        return;
    }
    const marker = parseJoinMarker(getPersistentItem(JOIN_TIMELINE_KEY), Date.now(), getCurrentBrowserRoomName());
    if (marker) {
        queueRoomNavigationStartedAt = marker.startedAt;
        queueRoomJoinSource = marker.source;
    }
}
/** Ms since this document started, for web stage events; absent on native. */
function sinceDocumentStartMs() {
    // `performance` is a browser global; the native lib does not declare it.
    const origin = globalThis.performance?.timeOrigin;
    // A room page a portal prerendered starts when it is shown, the same
    // start its vrs_web_room_load navigationToBootMs uses
    // (shared/prerender-gate.js; 0 everywhere else).
    const shownAt = globalThis.__vrsActivationStartMs;
    const activationStart = typeof shownAt === 'number' && Number.isFinite(shownAt) && shownAt > 0 ? shownAt : 0;
    return typeof origin === 'number' && Number.isFinite(origin)
        ? Math.max(0, Math.round(Date.now() - origin - activationStart)) : undefined;
}
function rememberJoinStart(data, source) {
    if (!data.roomName) {
        return;
    }
    try {
        setPersistentItem(JOIN_TIMELINE_KEY, serializeJoinMarker({
            callId: data.callId,
            requestId: data.requestId,
            roomName: data.roomName,
            source,
            startedAt: Date.now()
        }));
    }
    catch { /* storage blocked: the stage events still measure from boot */ }
}
function resetConferenceLifecycleState() {
    conferenceExitInProgressKey = undefined;
    conferenceJoinedForActiveCall = false;
    joinedConferenceForActiveCall = undefined;
    conferenceJoinFailure = undefined;
    preJoinExitHandledKey = undefined;
    queueRoomNavigationStartedAt = undefined;
    queueRoomSignalingConnectedAt = undefined;
    queueRoomMediaConnectedAt = undefined;
    queueRoomRemoteVideoAt = undefined;
    queueRoomJoinSource = undefined;
    activeConferenceJoinedAt = undefined;
    inCallMediaFailure = undefined;
    lastVideoChange = undefined;
}
// A freshly matched call is protected from reconciler eviction for this long,
// covering the web client's full page navigation into the room before the
// server's active-call record is observable to the poll.
const ACTIVE_CALL_EVICTION_GRACE_MS = 20000;
const ACTIVE_CALL_RECONCILE_INTERVAL_MS = 5000;
const ACTIVE_CALL_RECONCILE_LOOP_MS = 7000;
const VRI_LAST_CALL_END_NOTICE_KEY = 'vri_last_call_end_notice';
function getStoredActiveCall() {
    return getPersistentJson('vrs_active_call');
}
/**
 * The call a room stage event (signaling, media, first remote video) belongs
 * to. The stored call (vrs_active_call) is in localStorage and outlives its
 * call, so on a room page it counts only when it names that room; the join
 * marker for the room, written by the tap that opened it, comes first, as for
 * the room boot and load events (roomBootTelemetry.web.ts). A stale stored call split one join
 * into three timeline rows: boot by room, load by the new call, and these
 * stages by the old call with the old room's name (2026-09-23 review). Native
 * has no page path, so the stored call is used there as before.
 *
 * @returns {Object} The callId, requestId and roomName to report.
 */
function getStageEventCall() {
    const activeCall = getStoredActiveCall();
    const pageRoom = getCurrentBrowserRoomName();
    if (!pageRoom) {
        return {
            callId: activeCall?.callId,
            requestId: activeCall?.requestId,
            roomName: activeCall?.roomName
        };
    }
    const marker = parseJoinMarker(getPersistentItem(JOIN_TIMELINE_KEY), Date.now(), pageRoom);
    const roomCall = activeCall?.roomName
        && getBackendSafeRoomName(activeCall.roomName) === getBackendSafeRoomName(pageRoom) ? activeCall : undefined;
    return {
        callId: marker?.callId || roomCall?.callId,
        requestId: marker?.requestId || roomCall?.requestId,
        roomName: marker?.roomName || roomCall?.roomName || pageRoom
    };
}
function getActiveCallExitKey(activeCall) {
    return activeCall?.callId || activeCall?.roomName;
}
function getStoredQueueUser() {
    const user = getPersistentJson('vrs_user_info');
    return {
        id: user?.id || user?.userId,
        role: (getPersistentItem('vrs_user_role') || user?.role || '').toLowerCase()
    };
}
function wasEndedByLocalClient(data, endedByRole) {
    const currentUser = getStoredQueueUser();
    const endedBy = typeof data.endedBy === 'string' ? data.endedBy.trim() : '';
    const endedBySelf = Boolean(currentUser.id && endedBy && currentUser.id === endedBy);
    return currentUser.role === 'client' && (endedByRole === 'client' || endedBySelf);
}
/**
 * Persists match metadata to storage so it's available when the call ends.
 * groupCall is the server's word that a platform call is a group, which lets
 * its host end it for everyone (groupEndForAll.ts).
 */
function persistMatchData(data) {
    // The same call written again (a replayed join, a reconnect) keeps what
    // the record already knew when the new payload leaves it out: who placed
    // the call (the interpreter button), that it became a group, and its
    // interpreter. Robert, 2026-10-05: a group call lost the button.
    const stored = getStoredActiveCall();
    const same = data.callId && stored?.callId === data.callId ? stored : undefined;
    setPersistentItem('vrs_active_call', JSON.stringify({
        callerIdPhone: data.callerIdPhone,
        callId: data.callId,
        callType: data.callType,
        clientId: data.clientId ?? same?.clientId,
        clientStartWithVideoMuted: data.clientStartWithVideoMuted,
        groupCall: data.groupCall === true || same?.groupCall === true ? true : undefined,
        hostId: data.hostId ?? same?.hostId,
        roomName: data.roomName,
        requestId: data.requestId,
        interpreterName: data.interpreterName ?? same?.interpreterName,
        interpreterId: data.interpreterId ?? same?.interpreterId,
        clientName: data.clientName,
        language: data.language,
        // A direct call: the other person's photo for the waiting screen.
        peerPhotoUrl: data.peerPhotoUrl,
        requestKind: data.requestKind,
        startedAt: Date.now(),
        targetPhone: data.targetPhone,
        targetPhoneLast4: data.targetPhoneLast4,
        vcoEnabled: data.vcoEnabled,
        // Taken by transfer: the hearing party's live phone leg, which the
        // room joins instead of dialling (interpreter-phone/transferredPhoneLeg.ts).
        phoneLeg: data.phoneLeg,
        transferId: data.transferId,
        // Joined as a colleague's team interpreter (teamOffers.ts).
        team: data.team?.teamId ? data.team : undefined
    }));
    callStartTimestamp = Date.now();
    resetConferenceLifecycleState();
    emitMobileTelemetryEvent('queue_match_persisted', {
        hasCallId: Boolean(data.callId),
        hasRequestId: Boolean(data.requestId),
        hasRoomName: Boolean(data.roomName),
        language: data.language
    }, {
        callId: data.callId,
        requestId: data.requestId,
        roomName: data.roomName,
        state: 'matched'
    });
}
function hasStoredActiveCall() {
    const activeCall = getStoredActiveCall();
    return Boolean(activeCall?.callId || activeCall?.roomName);
}
function stopActiveCallReconciliationLoop() {
    if (!activeCallReconciliationTimer) {
        return;
    }
    clearInterval(activeCallReconciliationTimer);
    activeCallReconciliationTimer = undefined;
}
function startActiveCallReconciliationLoop(store) {
    if (activeCallReconciliationTimer || getPersistentItem('vrs_user_role') !== 'client') {
        return;
    }
    activeCallReconciliationTimer = setInterval(() => {
        if (!hasStoredActiveCall()) {
            stopActiveCallReconciliationLoop();
            return;
        }
        reconcileLocalActiveCall(store, 'active_call_timer_reconcile');
    }, ACTIVE_CALL_RECONCILE_LOOP_MS);
}
/**
 * Writes a local call history entry from the persisted active call data.
 */
function writeLocalCallHistory() {
    const activeCall = getStoredActiveCall();
    if (!activeCall?.callId) {
        return;
    }
    const duration = callStartTimestamp
        ? Math.round((Date.now() - callStartTimestamp) / 1000)
        : 0;
    const entry = {
        id: activeCall.callId,
        contactName: activeCall.clientName || 'Unknown',
        phoneNumber: '',
        direction: 'outgoing',
        duration,
        timestamp: new Date(activeCall.startedAt || Date.now()).toISOString(),
        interpreterName: activeCall.interpreterName
    };
    // Only the authenticated participant may own this local snapshot. An old
    // active-call record must not become the next account's recent history.
    const scope = currentCallHistoryScope();
    const participantId = scope?.owner.role === 'interpreter' ? activeCall.interpreterId : activeCall.clientId;
    if (scope && participantId === scope.owner.userId) {
        void appendCallHistoryCache(scope, entry).then(saved => {
            if (saved)
                mobileLog('info', 'call_history_entry_saved', { callId: entry.id, duration });
        });
    }
    else {
        mobileLog('warn', 'call_history_owner_unavailable');
    }
    callStartTimestamp = undefined;
}
function getLocalUserId() {
    const id = getPersistentJson('vrs_user_info')?.id;
    return typeof id === 'string' ? id : undefined;
}
/**
 * Names of the people in the current platform call, by account id, learned
 * from the ring, answer and join events. p2p_participant_left carries only
 * the id; "Someone left" is the fallback, never a wrong name.
 */
const platformCallPeerNames = new Map();
/**
 * Stand-ins the server sends when it knows no name: 'Unknown' (p2p_join_room
 * from a server older than the null fallback) and 'Malka contact' (the ring
 * and push fallback). They name nobody, so they are never remembered or
 * stored as a person (the room's partner pane would greet them as one).
 */
const PLACEHOLDER_PLATFORM_PEER_NAMES = new Set(['unknown', 'malka contact']);
function usablePlatformPeerName(name) {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    return trimmed && !PLACEHOLDER_PLATFORM_PEER_NAMES.has(trimmed.toLowerCase()) ? trimmed : undefined;
}
function rememberPlatformCallPeer(callId, userId, name) {
    const usable = usablePlatformPeerName(name);
    if (callId && userId && usable) {
        platformCallPeerNames.set(`${callId}:${userId}`, usable);
    }
}
/**
 * The other person's name for a direct call being entered: the one the ring
 * carried (the server's own record of the caller) first, then the name this
 * event offers, if it is a real one.
 */
function platformCallPeerName(callId, userId, offered) {
    const remembered = callId && userId ? platformCallPeerNames.get(`${callId}:${userId}`) : undefined;
    return remembered || usablePlatformPeerName(offered);
}
function isCurrentPlatformCall(callId) {
    const activeCall = getStoredActiveCall();
    return Boolean(callId && activeCall?.callId === callId && isPlatformCallRoomName(activeCall.roomName));
}
/**
 * The server says the current platform call is a group now: someone joined
 * it (p2p_participant_joined — a direct call becomes a group when a third
 * person is invited), or an answer said so (p2p_accepted.groupCall). Its
 * host may then end it for everyone. Never for another call.
 */
function markPlatformCallGroup(callId) {
    const activeCall = getPersistentJson('vrs_active_call');
    if (!callId || !activeCall || activeCall.callId !== callId || activeCall.groupCall === true
        || !isPlatformCallRoomName(typeof activeCall.roomName === 'string' ? activeCall.roomName : undefined)) {
        return;
    }
    setPersistentItem('vrs_active_call', JSON.stringify({ ...activeCall, groupCall: true }));
}
/**
 * Who hosts the current platform call now (hostId on p2p_accepted,
 * p2p_participant_joined and p2p_participant_left): the host passes to the
 * person seated longest when the host leaves (Robert, 2026-10-05), and End
 * call for everyone goes with it (isGroupCallHost). Never for another call.
 */
function recordPlatformCallHost(callId, hostId) {
    const activeCall = getPersistentJson('vrs_active_call');
    if (!callId || !hostId || !activeCall || activeCall.callId !== callId || activeCall.hostId === hostId
        || !isPlatformCallRoomName(typeof activeCall.roomName === 'string' ? activeCall.roomName : undefined)) {
        return;
    }
    setPersistentItem('vrs_active_call', JSON.stringify({ ...activeCall, hostId }));
}
/**
 * "Dana joined" / "Dana left" for everyone in a platform call. Deaf
 * participants have no audio cue that someone came or went, and platform
 * rooms get no Jitsi join/leave toasts (joinLeaveNotificationsDisabled).
 */
function showPlatformParticipantNotice(store, kind, data) {
    if (!data.userId || data.userId === getLocalUserId() || !isCurrentPlatformCall(data.callId)) {
        return;
    }
    rememberPlatformCallPeer(data.callId, data.userId, data.name);
    store.dispatch(showNotification({
        titleKey: undefined,
        title: participantNoticeTitle(kind, data.name || platformCallPeerNames.get(`${data.callId}:${data.userId}`), { rejoined: data.rejoined, removed: data.removed }),
        descriptionKey: undefined,
        uid: `p2p-participant-${kind}-${data.userId}`
    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
}
/**
 * The caller auto-enters the room on ringing (native), and a group caller
 * is in the room while others still ring, so a decline must be surfaced IN
 * the room. In a group call one decline changes nothing for everyone else:
 * it says who declined and keeps the call.
 */
function showP2PDeclinedNotice(store, data) {
    const roomName = getStoredActiveCall()?.roomName || getCurrentBrowserRoomName();
    const notice = declineNotice(data.calleeName, Boolean(roomName?.startsWith('group-')), store.getState()['features/base/participants'].remote.size > 0);
    store.dispatch(showNotification({
        titleKey: undefined,
        title: notice.title,
        descriptionKey: undefined,
        description: notice.description,
        uid: `p2p-declined-${data.calleeId || 'peer'}`,
        // The room closes: say it on Home too (AppNoticeHost).
        keepAfterCall: true
    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
}
/**
 * The web caller's direct ring was declined: back to the portal, which says
 * so where its ring bar used to (webOutgoingRing.ts). Leaving cancels the
 * call (p2p_cancel), since nobody answered it. The app keeps today's notice
 * in the room.
 *
 * @param {Object} store - The redux store.
 * @param {Object} data - The p2p_declined data.
 * @returns {void}
 */
function leaveDeclinedWebRing(store, data) {
    const ring = getOutgoingRingView();
    const stored = getStoredActiveCall();
    if (!data.callId || ring?.callId !== data.callId || stored?.callId !== data.callId || stored.groupCall === true) {
        return;
    }
    rememberCallEndNotice(data.callId, `${data.calleeName || 'They'} declined your call.`);
    // Straight out, even before the room has joined: a hang-up would wait for it.
    finalizeConferenceExit(store, 'outgoing_ring_declined');
}
/**
 * Web: the portal opened this room while the call still rings, so this page
 * takes the ring over (webOutgoingRing.ts). Once anyone answers or the call
 * is over, the marker leaves the stored call, so a reload never brings the
 * ring back.
 *
 * @returns {void}
 */
function resumeWebOutgoingRing() {
    const stored = getStoredActiveCall();
    const ring = storedOutgoingRing(stored, getCurrentBrowserRoomName());
    if (!stored || !ring) {
        return;
    }
    if (!resumeOutgoingRing(ring)) {
        // Too old to ring (a reload long after): leaving ends the call as usual.
        setPersistentItem('vrs_active_call', JSON.stringify(withoutOutgoingRing(stored)));
        return;
    }
    // The no-answer report closes this room as it closes the app's.
    lastPlatformCall = {
        callId: ring.callId,
        roomName: String(stored.roomName)
    };
    const unsubscribe = subscribeOutgoingRing(() => {
        if (getOutgoingRingView()?.callId === ring.callId) {
            return;
        }
        unsubscribe();
        const current = getStoredActiveCall();
        if (current?.callId === ring.callId && current.outgoingRing) {
            setPersistentItem('vrs_active_call', JSON.stringify(withoutOutgoingRing(current)));
        }
    });
}
/**
 * Nobody answered a group call. On web the caller is still on Home, which
 * closes its ringing bar; a caller parked alone in the room (native enters
 * on ringing) is told here, and told apart: "Everyone declined your call."
 * or "No one answered." The call_ended the server sends next closes the
 * room. Never for another call.
 */
function handleP2PGroupNoAnswer(store, data) {
    if (!data.callId || (lastPlatformCall?.callId !== data.callId && !isCurrentPlatformCall(data.callId))) {
        return;
    }
    store.dispatch(showNotification({
        titleKey: undefined,
        title: groupNoAnswerText(data),
        descriptionKey: undefined,
        uid: 'p2p-group-no-answer',
        // The room closes: say it on Home too (AppNoticeHost).
        keepAfterCall: true
    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
}
/**
 * The interpreter's call-back, while they wait in its room
 * (interpreter_callback_status; docs/interpreted-group-calls.md): who is being
 * rung and who has answered, with Cancel call back until the person they were
 * with is back.
 */
function showInterpreterCallbackStatus(store, data) {
    const view = callbackStatusView(data);
    const callbackCallId = data.callbackCallId;
    const uid = 'interpreter-callback-status';
    store.dispatch(hideNotification(uid));
    // Closed by the interpreter's own Cancel: nothing to say.
    if (!view.title) {
        return;
    }
    store.dispatch(showNotification({
        titleKey: undefined,
        title: view.title,
        descriptionKey: undefined,
        description: view.lines.join('\n') || undefined,
        uid,
        ...view.cancellable && callbackCallId ? {
            customActionNameKey: [view.cancelLabel],
            customActionType: [BUTTON_TYPES.DESTRUCTIVE],
            customActionHandler: [() => {
                    queueService.cancelInterpreterCallback(callbackCallId);
                    return true;
                }]
        } : {}
    }, view.cancellable ? NOTIFICATION_TIMEOUT_TYPE.STICKY : NOTIFICATION_TIMEOUT_TYPE.LONG));
}
/**
 * A group call is ringing. The native caller goes straight into the room,
 * so the room's invite panel starts from this first round, and the caller
 * is told there who is not being rung: handles or emails that matched no
 * one, and people with Do Not Disturb on. The web caller waits on Home,
 * whose ringing bar says the same.
 */
function handleGroupRinging(store, data) {
    if (data.groupCall !== true) {
        return;
    }
    rememberGroupRinging(data);
    const gaps = groupRingGaps(data);
    if (gaps && isNativeRuntime()) {
        store.dispatch(showNotification({
            titleKey: undefined,
            title: gaps,
            descriptionKey: undefined,
            uid: `p2p-group-ring-gaps-${data.callId || 'call'}`
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    }
}
/*
 * Outside meetings (docs/outside-meeting-desk.md, meetingRemote.ts). The
 * interpreter's web console (the desk) opens the meeting. Malka Terp is the
 * remote control: it never gets the link and never opens a room.
 */
// After a sign-in during a meeting call, the server sends the call again
// (external_meeting_call). If it does not in this time, the call ended while
// this phone was away (a dropped socket missed call_ended).
const MEETING_SNAPSHOT_WAIT_MS = 10000;
let meetingSnapshotWait;
function stopMeetingSnapshotWait() {
    if (meetingSnapshotWait) {
        clearTimeout(meetingSnapshotWait.timer);
        meetingSnapshotWait = undefined;
    }
}
/**
 * call_ended for the interpreter's outside-meeting call: the card goes and
 * the wrap-up starts, as after a call in a room. There is no room to leave.
 * Returns false for any other call.
 */
function endInterpreterMeetingCall(store, data) {
    const call = store.getState?.()?.['features/interpreter-queue']?.interpreterMeetingCall;
    if (!call || !data.callId || call.callId !== data.callId) {
        return false;
    }
    const endReason = typeof data.endReason === 'string' ? data.endReason
        : typeof data.reason === 'string' ? data.reason : 'remote_call_ended';
    stopMeetingSnapshotWait();
    store.dispatch({ type: INTERPRETER_MEETING_CALL_ENDED, callId: call.callId });
    mobileLog('info', 'interpreter_meeting_call_ended', {
        endedByRole: typeof data.endedByRole === 'string' ? data.endedByRole : undefined,
        entered: Boolean(call.enteredAt)
    }, { console: false });
    beginInterpreterWrapUp(store, { callId: call.callId, startedAt: call.enteredAt || call.knownAt }, { native: isNativeRuntime() });
    store.dispatch(interpretedCallEnded(endReason));
    return true;
}
/**
 * The interpreter accepted an outside meeting (request_accepted). It opens on
 * their web console (the desk), never here: no Malka room, and this phone
 * gets no link. Home shows the call and its steps
 * (InterpreterMeetingCallCard), and external_meeting_call follows with the
 * full call. The server keeps the interpreter out of the queue until the call
 * ends, so a reconnect must not announce Available again.
 */
function startInterpreterMeetingCall(store, data) {
    const queue = store.getState()['features/interpreter-queue'];
    const meetingCall = meetingCallFromAccept(data, queue?.meetingDesk?.device, Date.now());
    queueService.holdAvailabilityReplay();
    if (meetingCall && queue?.interpreterMeetingCall?.callId !== meetingCall.callId) {
        // A new call ends the last call's wrap-up (and its return to the queue).
        store.dispatch(dismissInterpreterWrapUp());
        store.dispatch({ type: INTERPRETER_MEETING_CALL_UPDATED, interpreterMeetingCall: meetingCall });
    }
    mobileLog('info', 'interpreter_meeting_call_accepted', { platform: meetingCall?.platform || null }, { console: false });
}
// Refusals of external_meeting_status (vrs-server/ws/external-meeting-handlers.js)
// that carry no operation.
const MEETING_STEP_REFUSAL_CODES = new Set([
    'CALL_ENDED',
    'EXTERNAL_MEETING_CALL_NOT_FOUND',
    'EXTERNAL_MEETING_END_REASON_REQUIRED',
    'EXTERNAL_MEETING_NOT_ENTERED'
]);
/**
 * A refused step of the outside-meeting call goes to its card. A call that
 * the server ended or does not know goes away, and Home shows the message.
 * Returns true when the card took the refusal.
 */
function meetingStepRefused(store, data) {
    const call = store.getState?.()?.['features/interpreter-queue']?.interpreterMeetingCall;
    const ours = data.operation === 'external_meeting_status'
        || (!data.operation && MEETING_STEP_REFUSAL_CODES.has(data.code || ''));
    if (!call?.pending || !ours) {
        return false;
    }
    mobileLog('warn', 'interpreter_meeting_step_refused', { code: data.code, event: call.pending }, { console: false });
    if (data.code === 'CALL_ENDED' || data.code === 'EXTERNAL_MEETING_CALL_NOT_FOUND') {
        stopMeetingSnapshotWait();
        store.dispatch({ type: INTERPRETER_MEETING_CALL_ENDED, callId: call.callId });
        return false;
    }
    store.dispatch({
        type: INTERPRETER_MEETING_STEP_REFUSED,
        callId: call.callId,
        error: data.message || 'The step was not recorded. Try again.'
    });
    return true;
}
function handleLinkedInterpretedCallEnded(store, data) {
    // A group participant's own departure reaches them as call_ended naming
    // them. Anyone else holding that record stays in the call: only a real
    // end of the call ends the room, never one person leaving.
    if (isOtherParticipantDeparture(data, getLocalUserId())) {
        mobileLog('info', 'platform_call_departure_ignored', { callId: data.callId }, { console: false });
        return;
    }
    // This device's client ended the VRI session (End call): its room is
    // closing through endConference, and finalizeConferenceExit takes it home.
    if (vriSessionEndSentFor && data.callId === vriSessionEndSentFor) {
        removePersistentItem(VRI_LAST_CALL_END_NOTICE_KEY);
        return;
    }
    const endedByRole = typeof data.endedByRole === 'string'
        ? data.endedByRole
        : data.endedBy === 'admin' ? 'admin' : undefined;
    const endReason = typeof data.endReason === 'string'
        ? data.endReason
        : typeof data.reason === 'string' ? data.reason : undefined;
    const activeCall = getPersistentJson('vrs_active_call');
    const eventCallId = data.callId?.trim();
    const eventRoomName = getQueueRoomName(data);
    const callIdMatches = Boolean(activeCall?.callId && eventCallId && activeCall.callId === eventCallId);
    const roomNameMatches = Boolean(activeCall?.roomName && eventRoomName && activeCall.roomName === eventRoomName);
    if (!callIdMatches && !roomNameMatches) {
        return;
    }
    writeLocalCallHistory();
    // This interpreter handed the call to a colleague, who has it now
    // (vrs-server/ws/call-transfer-handover.js): they leave quietly. Their
    // device lets go of a phone leg that went to the colleague, and hangs up
    // one that did not (phoneLegMoved). Their own transfer needs no notice;
    // one an admin made names who took it.
    const transferredOut = data.surface === 'call_transferred' || data.reason === 'call_transferred';
    if (transferredOut) {
        removePersistentItem(VRI_LAST_CALL_END_NOTICE_KEY);
        if (endedByRole === 'admin') {
            setPersistentItem(VRI_LAST_CALL_END_NOTICE_KEY, JSON.stringify({
                endedAt: Date.now(),
                message: data.transferredToName ? `Transferred to ${data.transferredToName}.` : 'Call transferred.',
                reason: 'call_transferred'
            }));
        }
    }
    else if (wasEndedByLocalClient(data, endedByRole)) {
        removePersistentItem(VRI_LAST_CALL_END_NOTICE_KEY);
    }
    else {
        setPersistentItem(VRI_LAST_CALL_END_NOTICE_KEY, JSON.stringify({
            endedAt: Date.now(),
            message: data.surface === 'reconnect_grace_expired'
                ? `The ${endedByRole === 'interpreter' ? 'interpreter' : 'client'} disconnected.`
                : endedByRole === 'admin'
                    ? 'The session was ended by an administrator.'
                    : endedByRole === 'interpreter'
                        ? 'The interpreter ended the session.'
                        : endedByRole === 'client'
                            ? 'The client ended the session.'
                            : 'The session ended.',
            reason: endReason || endedByRole || 'remote_call_ended'
        }));
    }
    removePersistentItem('vrs_active_call');
    removePersistentItem('vri_pending_invite_tokens');
    removePersistentItem('vri_pending_invite_url');
    removePersistentItem('vri_pending_invite_draft');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    resetConferenceLifecycleState();
    forgetMediaRejoin();
    // Say who ended it, out loud, on the device.
    //
    // This was telemetry-only, so answering "what hung that call up?" needed
    // the admin panel — and when the panel is unreachable, or the row's payload
    // is not expandable, the answer was unavailable at exactly the moment it
    // was wanted. endedBy/endedByRole/surface now also reach the browser
    // console and the mobile log buffer, where a user can read them back
    // without any operator access at all.
    const endDetails = {
        callId: activeCall?.callId || eventCallId,
        endedBy: typeof data.endedBy === 'string' ? data.endedBy : undefined,
        endedByRole,
        reason: endReason || endedByRole || 'remote_call_ended',
        roomName: activeCall?.roomName || eventRoomName,
        surface: typeof data.surface === 'string' ? data.surface : undefined
    };
    // The same handler ends every call: a direct or group call that never had
    // an interpreter logs call_ended_remote (testers read
    // interpreted_call_ended_remote in plain P2P tests as an interpreter fault,
    // 2026-10-13). A call with an interpreter keeps the old name.
    const endEvent = isPlatformCallRoom(endDetails.roomName) && !activeCall?.interpreterName
        ? 'call_ended_remote'
        : 'interpreted_call_ended_remote';
    mobileLog('warn', endEvent, endDetails);
    emitMobileTelemetryEvent(endEvent, {
        endedBy: endDetails.endedBy,
        endedByRole,
        reason: endDetails.reason,
        surface: endDetails.surface
    }, {
        callId: endDetails.callId,
        roomName: endDetails.roomName,
        state: 'ended'
    });
    // The host ended a group call for everyone, or the person an interpreter
    // was tied to left or dropped, which ends it for everyone too: "Dana
    // left." (docs/interpreted-group-calls.md). The room closes at once, so
    // Home shows it too.
    const forAllNotice = anchorEndNotice(data, getLocalUserId()) || endedForAllNotice(data, getLocalUserId());
    if (forAllNotice) {
        rememberCallEndNotice(activeCall?.callId || eventCallId, forAllNotice);
        store.dispatch(showNotification({
            titleKey: undefined,
            title: forAllNotice,
            descriptionKey: undefined,
            uid: 'p2p-ended-for-all',
            // The room closes: say it on Home too (AppNoticeHost).
            keepAfterCall: true
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    }
    // After the person they were with dropped, the interpreter may call
    // everyone back; the wrap-up offers it and stays paused until they choose.
    beginInterpreterWrapUp(store, activeCall, {
        callback: wrapUpCallbackOffer(data),
        native: isNativeRuntime()
    });
    store.dispatch(interpretedCallEnded(endReason || endedByRole || 'remote_call_ended'));
    if (transferredOut) {
        leaveTransferredCall(store, activeCall, data.phoneLegMoved === true);
        return;
    }
    store.dispatch(hangup(false));
    routeToRoleHome(activeCall);
}
// Longest the room waits for its phone audio to let go before leaving.
const TRANSFER_RELEASE_WAIT_MS = 1500;
/**
 * Leave a call handed to a colleague, then the room without ending it. First
 * the phone: a leg that went to the colleague is let go
 * (twilioVoiceService.releaseForTransfer, which forgets this device's attempt
 * so its next call can dial); one still bound to this device is hung up, as
 * at any call's end, so the hearing party is not left on a silent line.
 */
function leaveTransferredCall(store, activeCall, phoneLegMoved) {
    let left = false;
    const leave = () => {
        if (left)
            return;
        left = true;
        store.dispatch(hangup(false));
        routeToRoleHome(activeCall);
    };
    void Promise.resolve()
        .then(() => (phoneLegMoved ? twilioVoiceService.releaseForTransfer() : twilioVoiceService.hangupCall()))
        .catch(() => undefined)
        .then(leave);
    setTimeout(leave, TRANSFER_RELEASE_WAIT_MS);
}
function clearLocalActiveCall(store, reason) {
    const activeCall = getStoredActiveCall();
    noteCallEndedHere(activeCall);
    removePersistentItem('vrs_active_call');
    removePersistentItem('vri_pending_invite_tokens');
    removePersistentItem('vri_pending_invite_url');
    removePersistentItem('vri_pending_invite_draft');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    resetConferenceLifecycleState();
    forgetMediaRejoin();
    mobileLog('info', 'vrs_local_active_call_cleared', {
        callId: activeCall?.callId,
        reason,
        roomName: activeCall?.roomName
    });
    emitMobileTelemetryEvent('vrs_local_active_call_cleared', {
        reason
    }, {
        callId: activeCall?.callId,
        roomName: activeCall?.roomName,
        state: 'cleared'
    });
    beginInterpreterWrapUp(store, activeCall, { native: isNativeRuntime() });
    store.dispatch(interpretedCallEnded(reason));
    store.dispatch(hangup(false));
    routeToRoleHome(activeCall);
}
// The VideoMail recording rooms (voicemail/sessionHandoff.ts RECORDING_ROOM).
// Kept here, not imported: the sandboxed harnesses that load this file map
// each import by name.
const VIDEOMAIL_RECORDING_ROOM = /^(voicemail|videomail-greeting)-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * Whether this page is in a VideoMail recording room that is not the stored
 * call's room.
 *
 * @param {Object} store - The redux store.
 * @param {string} [storedRoomName] - The stored call's room.
 * @returns {boolean}
 */
function isOtherRoomThanVideoMailRecording(store, storedRoomName) {
    const room = getCurrentRoomName(store);
    return Boolean(room && VIDEOMAIL_RECORDING_ROOM.test(room)
        && getBackendSafeRoomName(room) !== getBackendSafeRoomName(storedRoomName || ''));
}
/**
 * Removes the stored call that the server no longer has, while this page
 * records a VideoMail: no hang-up, no navigation, the recording goes on.
 *
 * @param {StoredActiveCall} activeCall - The stored call.
 * @param {string} reason - Why the check ran.
 * @returns {void}
 */
function forgetCallBeforeVideoMail(activeCall, reason) {
    removePersistentItem('vrs_active_call');
    stopActiveCallReconciliationLoop();
    mobileLog('info', 'vrs_call_before_videomail_forgotten', {
        callId: activeCall.callId,
        reason,
        roomName: activeCall.roomName
    }, { console: false });
    emitMobileTelemetryEvent('vrs_call_before_videomail_forgotten', { reason }, {
        callId: activeCall.callId,
        roomName: activeCall.roomName,
        state: 'cleared'
    });
}
function reconcileLocalActiveCall(store, reason) {
    const activeCall = getStoredActiveCall();
    if (!activeCall?.callId) {
        return;
    }
    if (getPersistentItem('vrs_user_role') !== 'client') {
        return;
    }
    const now = Date.now();
    if (activeCallReconciliationInFlight || now - lastActiveCallReconciliationAt < ACTIVE_CALL_RECONCILE_INTERVAL_MS) {
        return;
    }
    activeCallReconciliationInFlight = true;
    lastActiveCallReconciliationAt = now;
    const isCurrentCall = () => {
        const currentCall = getStoredActiveCall();
        return getPersistentItem('vrs_user_role') === 'client'
            && currentCall?.callId === activeCall.callId
            && currentCall?.roomName === activeCall.roomName;
    };
    apiClient.get('/api/client/active-call').then(response => {
        // This request may finish after a new match, local end or role change.
        // Its answer cannot clear or report a failure against that newer state.
        if (!isCurrentCall())
            return;
        if (response.error) {
            mobileLog('warn', 'vrs_active_call_reconcile_failed', {
                error: response.error,
                reason
            }, { console: false });
            return;
        }
        const serverCall = response.data?.call || null;
        const callIdMatches = Boolean(serverCall?.id && serverCall.id === activeCall.callId);
        const roomNameMatches = Boolean(serverCall?.room_name && serverCall.room_name === activeCall.roomName);
        if (!serverCall || (!callIdMatches && !roomNameMatches)) {
            // This page records a VideoMail, and the stored call is the one
            // before it (the unanswered call): that call is over, and the
            // recording is not. Forget it here without the hang-up and Home,
            // which ended Devin's web takes 7 s after the room opened
            // (2026-10-27).
            if (isOtherRoomThanVideoMailRecording(store, activeCall.roomName)) {
                forgetCallBeforeVideoMail(activeCall, reason);
                return;
            }
            // The server names another live call while this device is in the
            // stored call's room: a ring placed to this person during the call
            // (Robert, 2026-10-19, iPhone 1:1: this hung up the call he was in).
            // Keep the call. Only "no call at all" ends it here.
            const conferenceState = store.getState?.()?.['features/base/conference'];
            const joinedHere = Boolean(conferenceState?.conference && activeCall.roomName
                && String(conferenceState.room || '').toLowerCase() === activeCall.roomName.toLowerCase());
            if (serverCall && joinedHere) {
                mobileLog('info', 'vrs_active_call_reconcile_kept_joined', {
                    callId: activeCall.callId,
                    reason,
                    serverCallId: serverCall.id
                }, { console: false });
                return;
            }
            // Never evict a call that has only just started. The web client
            // reaches the room through a full page navigation, so there is a
            // window where the browser is already in the room while the
            // server's active-call record is not yet visible to this poll —
            // clearing there would hang up a perfectly good call and bounce
            // the client home. Native stores startedAt as a number, web as an
            // ISO string; accept both and fail safe (skip) if unparseable.
            const startedAtRaw = activeCall.startedAt;
            const startedAt = typeof startedAtRaw === 'number'
                ? startedAtRaw
                : Date.parse(String(startedAtRaw ?? ''));
            if (!Number.isFinite(startedAt) || Date.now() - startedAt < ACTIVE_CALL_EVICTION_GRACE_MS) {
                return;
            }
            clearLocalActiveCall(store, reason);
        }
    }).catch(() => {
        if (isCurrentCall()) {
            mobileLog('warn', 'vrs_active_call_reconcile_failed', {
                error: 'ACTIVE_CALL_RECONCILE_REQUEST_FAILED',
                reason
            }, { console: false });
        }
    }).finally(() => {
        activeCallReconciliationInFlight = false;
    });
}
/**
 * Send the user back to their role's home surface after a call ends or its
 * local state is cleared.
 *
 * resetRoot is React Navigation, which exists only in the native app: on web
 * rootNavigationRef.current is null, so every call here returned undefined and
 * navigated NOWHERE. That silence is what made the web client loop — the
 * reconciler would hang up the call and delete vrs_active_call, then leave the
 * browser sitting on the room URL with no call, where the conference promptly
 * tried to re-establish itself and the cycle repeated until the tab was
 * closed. Native clients were unaffected because for them resetRoot works, so
 * the same code path recovered cleanly. Web needs a real navigation.
 */
function isNativeRuntime() {
    return typeof navigator !== 'undefined'
        && navigator.product === 'ReactNative';
}
function routeToRoleHome(endedCall) {
    const userRole = getPersistentItem('vrs_user_role');
    const isNative = isNativeRuntime();
    if (!isNative && typeof window !== 'undefined' && window.location) {
        // Same role → home mapping the web sign-in uses (WelcomePage.web).
        window.location.href = userRole === 'interpreter'
            ? '/interpreter-profile.html'
            : userRole === 'captioner'
                ? '/captioner-profile.html'
                : '/';
        return;
    }
    if (userRole === 'interpreter') {
        resetRoot(screen.interpreter.home);
        return;
    }
    // A VRI session started from the MalkaVRS app (dual-service accounts)
    // returns to the VRI console it came from.
    resetRoot(isVriApp() || isVriCall(endedCall) ? screen.vri.console : screen.vrs.home);
}
/**
 * Ends the stored call on this device: history, ring cancel or call end, the
 * stored call and its leftovers cleared, telemetry, wrap-up. Then, unless
 * `navigate` is false, hangs up and goes to the role's Home. With false the
 * caller navigates next itself (CALL_END_BEFORE_NAVIGATION, "Leave video
 * message"), so no hang-up navigation races it.
 *
 * @param {Object} store - The store to dispatch to.
 * @param {string} reason - Logged with the exit.
 * @param {Object} [options] - `navigate`: hang up and go Home (default true).
 * @returns {void}
 */
function finalizeConferenceExit(store, reason, { navigate = true } = {}) {
    const activeCall = getStoredActiveCall();
    const exitKey = getActiveCallExitKey(activeCall);
    if (!exitKey || conferenceExitInProgressKey === exitKey) {
        return;
    }
    conferenceExitInProgressKey = exitKey;
    vriSessionEndSentFor = undefined;
    // This device ended it: not a reason to refuse the next call, and not a
    // call for Home to adopt while the server still shows it (activeCallGate).
    noteCallEndedHere(activeCall);
    writeLocalCallHistory();
    // A page going away is not a hang-up: the server decides (the anchor of an
    // interpreted group is held for the reconnect grace; anyone else leaves).
    // Leaving a call this device placed before anyone answered cancels the
    // ring instead (p2p_cancel: zero duration, no CDR; outgoingRing.ts).
    if (reason === 'page_unload' || !cancelOutgoingRingOnExit(activeCall?.callId)) {
        queueService.endActiveCall(reason === 'page_unload' ? 'page_exit' : undefined);
    }
    removePersistentItem('vrs_active_call');
    removePersistentItem('vrs_own_queue_request');
    removePersistentItem('vri_pending_invite_tokens');
    removePersistentItem('vri_pending_invite_url');
    removePersistentItem('vri_pending_invite_draft');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    forgetMediaRejoin();
    mobileLog('info', 'vrs_conference_exit_finalized', { reason }, { console: false });
    emitMobileTelemetryEvent('vrs_conference_exit_finalized', { reason }, {
        callId: activeCall?.callId,
        requestId: activeCall?.requestId,
        roomName: activeCall?.roomName,
        state: 'exiting'
    });
    resetConferenceLifecycleState();
    beginInterpreterWrapUp(store, activeCall, { native: isNativeRuntime() });
    store.dispatch(interpretedCallEnded(reason));
    if (navigate) {
        store.dispatch(hangup(false));
        routeToRoleHome(activeCall);
    }
}
const CALL_PARTICIPANT_AWAY_UID = 'call-participant-away';
// How long the server holds a call for someone whose page went away
// (auth_success.capabilities.reconnectGraceMs); 0 means leaving ends it, and
// unknown (not yet signed in) is taken as held.
let reconnectGraceMs;
// The call whose room this device last reported as entered since signing in.
let roomEnteredSentFor;
/**
 * Whether a held-call notice is about the call on this device.
 *
 * @param {string} [callId] - The call the notice names.
 * @returns {boolean}
 */
function callNoticeBelongsHere(callId) {
    const activeCall = getStoredActiveCall();
    return Boolean(callId && activeCall?.callId === callId);
}
/**
 * The anchor of an interpreted group call changed (p2p_anchor_changed): the
 * anchor left and the call goes on, or the person who asked came back. The
 * stored call names the new anchor, and the banner that the old one is away
 * goes. Nobody is told about billing (Robert, 2026-10-06: VRS billing is the
 * back end's; VRI stays with the main account).
 *
 * @param {Object} store - The redux store.
 * @param {Object} data - The p2p_anchor_changed payload.
 * @returns {void}
 */
function handleP2PAnchorChanged(store, data) {
    const callId = typeof data?.callId === 'string' ? data.callId : undefined;
    const anchorId = typeof data?.anchorId === 'string' ? data.anchorId : undefined;
    if (!callId || !anchorId || !callNoticeBelongsHere(callId)) {
        return;
    }
    const anchorName = typeof data?.anchorName === 'string' && data.anchorName ? data.anchorName : undefined;
    recordP2PInterpreterOnCall(callId, undefined, { anchorId, anchorName });
    if (typeof data?.previousAnchorId === 'string') {
        store.dispatch({ type: VRS_PEER_RECONNECTED, callId, userId: data.previousAnchorId });
    }
    store.dispatch(hideNotification(CALL_PARTICIPANT_AWAY_UID));
    mobileLog('info', 'vrs_p2p_anchor_changed', { callId, local: anchorId === getLocalUserId() }, { console: false });
}
/**
 * Who dropped out, as the other side reads it: the interpreter by role, the
 * client by name when there is one.
 *
 * @param {string} [role] - Their role.
 * @param {string|null} [name] - Their display name.
 * @returns {string}
 */
function participantLabel(role, name) {
    if (role === 'interpreter') {
        return 'Your interpreter';
    }
    return name?.trim() || 'The caller';
}
/**
 * Whether a call-move message is about the call on this device: the stored
 * call, or (web) the room this page is showing.
 */
function callMoveBelongsHere(data) {
    const activeCall = getStoredActiveCall();
    if (data.callId && activeCall?.callId) {
        return activeCall.callId === data.callId;
    }
    const pageRoom = getCurrentBrowserRoomName();
    return Boolean(data.roomName && pageRoom
        && getBackendSafeRoomName(data.roomName) === getBackendSafeRoomName(pageRoom));
}
/**
 * The client's call moved to another of their devices (call_moved_elsewhere).
 * Leave the room WITHOUT ending the call: the local call record goes first,
 * so the conference-exit path finds nothing to finalize and sends no
 * call_end (the queue service also refuses call_end for a moved call), then
 * say where the call went. "Move it back here" restores the record and
 * reopens this room asking the server to move the call back.
 */
function leaveMovedCall(store, data) {
    if (!callMoveBelongsHere(data)) {
        return;
    }
    const activeCall = getStoredActiveCall();
    const saved = activeCall && (!data.callId || activeCall.callId === data.callId) ? activeCall : null;
    const pageActions = accountDevicePageActions();
    const moveBack = pageActions.moveBack;
    removePersistentItem('vrs_active_call');
    removePersistentItem('vrs_own_queue_request');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    resetConferenceLifecycleState();
    mobileLog('info', 'vrs_call_moved_elsewhere', { callId: data.callId }, { console: false });
    emitMobileTelemetryEvent('vrs_call_moved_elsewhere', {}, {
        callId: data.callId,
        roomName: data.roomName || undefined,
        state: 'moved'
    });
    store.dispatch(interpretedCallEnded('moved_to_other_device'));
    leaveConferenceWithoutEnding(store.dispatch);
    dismissAccountDeviceNotice('moving');
    // The app has no page to reload: it opens the room again itself, asking
    // for the move, and waits on Home meanwhile.
    if (isNativeRuntime()) {
        routeToRoleHome();
        presentAccountDeviceNotice(moveNotice('moved', data), saved?.callId && saved.roomName ? {
            home: () => undefined,
            moveBack: () => openCallMovedHere(store, { callId: saved.callId, roomName: saved.roomName }, saved)
        } : { home: () => undefined });
        return;
    }
    presentAccountDeviceNotice(moveNotice('moved', data), moveBack ? {
        home: routeToRoleHome,
        moveBack: () => {
            if (saved) {
                setPersistentItem('vrs_active_call', JSON.stringify(saved));
            }
            moveBack();
        }
    } : {});
}
/**
 * The app opens the account's call on this device, asking the server to move
 * it here ("Move call here", "Move it back here"). Make-before-break: the
 * room token is minted with moveCall (vrs-auth/jitsiRoomToken.ts), the other
 * device keeps the call until this device's media is up (call_room_joined on
 * DATA_CHANNEL_OPENED), and a room that refuses this device leaves it there.
 * The call is recorded like a matched call first, so leaving it hangs up.
 *
 * @param {Object} store - The redux store.
 * @param {Object} call - The call and its room.
 * @param {Object} [saved] - Its earlier record on this device, when there is one.
 * @returns {void}
 */
function openCallMovedHere(store, call, saved) {
    const current = getStoredActiveCall();
    if (!call.callId || !call.roomName || (current?.callId && current.callId !== call.callId)) {
        store.dispatch(showNotification({ title: 'Finish this call first', uid: 'account-device-move-refused' }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    setPersistentItem('vrs_active_call', JSON.stringify({
        // The same call already stored here keeps its fields (clientId: the
        // interpreter button) when the move carries no saved record.
        ...current?.callId === call.callId ? current : {},
        ...saved || {},
        callId: call.callId,
        roomName: call.roomName,
        startedAt: typeof saved?.startedAt === 'number' ? saved.startedAt : Date.now()
    }));
    callStartTimestamp = Date.now();
    queueService.forgetCallMovedAway(call.callId);
    resetConferenceLifecycleState();
    movedHereSignalledFor = undefined;
    lastPlatformCall = { callId: call.callId, roomName: call.roomName };
    setCallElsewhere(store, undefined);
    startActiveCallReconciliationLoop(store);
    mobileLog('info', 'vrs_call_move_here_requested', { callId: call.callId }, { console: false });
    emitMobileTelemetryEvent('vrs_call_move_here_requested', {}, {
        callId: call.callId,
        roomName: call.roomName,
        state: 'joining'
    });
    try {
        const result = store.dispatch(appNavigate(`${getRoomNavigationTarget(call.roomName)}?moveCall=1`, {
            hidePrejoin: true,
            startWithAudioMuted: false,
            startWithVideoMuted: getClientStartWithVideoMuted(saved?.clientStartWithVideoMuted)
        }));
        if (result && typeof result.catch === 'function') {
            result.catch(error => mobileLog('error', 'vrs_call_move_here_failed', conferenceErrorDetails(error), { console: false }));
        }
    }
    catch (error) {
        mobileLog('error', 'vrs_call_move_here_failed', conferenceErrorDetails(error), { console: false });
    }
}
// The client's call open on another device: re-asked on this timer while it
// is shown, as the web portal does, so the row clears when that call ends.
const CALL_ELSEWHERE_RECHECK_MS = 10000;
let callElsewhereTimer;
/**
 * Show (or clear) "In a call on another device" on this client's Home. Never
 * for the call this device is in itself.
 */
function setCallElsewhere(store, hold) {
    const own = getStoredActiveCall();
    const next = hold?.callId && hold.roomName && own?.callId !== hold.callId
        ? { callId: hold.callId, checkedAt: Date.now(), device: hold.device, roomName: hold.roomName, since: hold.since }
        : undefined;
    store.dispatch(accountCallElsewhereChanged(next));
    if (next && !callElsewhereTimer) {
        callElsewhereTimer = setInterval(() => queueService.requestAccountCallStatus(), CALL_ELSEWHERE_RECHECK_MS);
    }
    else if (!next && callElsewhereTimer) {
        clearInterval(callElsewhereTimer);
        callElsewhereTimer = undefined;
    }
}
/**
 * Call waiting (call-management/callWaitingSwitch.ts): this device answered a
 * call that rang during the one it is in, and the server sent the new call's
 * room. The current call is left first — ended for "Answer", held for
 * "Hold" — so the conference exit that follows finds no call of its own to
 * end, and the new room becomes the stored call.
 *
 * @param {Object} store - The redux store.
 * @param {CallWaitingAnswer} answer - How this device answered.
 * @param {string} [nextCallId] - The call it is going into.
 * @returns {void}
 */
function leaveCallForWaitingCall(store, answer, nextCallId) {
    const activeCall = getStoredActiveCall();
    if (answer.action === 'reject' || !activeCall?.callId || activeCall.callId === nextCallId) {
        return;
    }
    const holding = answer.action === 'hold_and_accept';
    const held = holding ? heldCallFromRecord(activeCall) : null;
    if (holding) {
        removePersistentItem('vrs_active_call');
    }
    else {
        writeLocalCallHistory();
        queueService.endActiveCall();
    }
    removePersistentItem('vrs_own_queue_request');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    forgetMediaRejoin();
    resetConferenceLifecycleState();
    mobileLog('info', 'vrs_call_waiting_switch', { action: answer.action }, { console: false });
    emitMobileTelemetryEvent('vrs_call_waiting_switch', { action: answer.action }, {
        callId: activeCall.callId,
        roomName: activeCall.roomName,
        state: holding ? 'held' : 'exiting'
    });
    store.dispatch(interpretedCallEnded(holding ? 'held_for_waiting_call' : 'ended_for_waiting_call'));
    if (held) {
        store.dispatch(heldCallSet(held));
    }
}
/**
 * "Resume": back into the call this device put on hold, which comes off hold
 * for the others in it (call_hold onHold:false). Its record goes back as it
 * was, so hanging up there ends it as before.
 *
 * @param {Object} store - The redux store.
 * @param {string} callId - The held call.
 * @returns {void}
 */
function resumeHeldCallHere(store, callId) {
    const held = store.getState()['features/call-management']?.heldCall;
    if (!held || held.callId !== callId) {
        return;
    }
    if (getStoredActiveCall()?.callId) {
        store.dispatch(showNotification({ title: 'Finish this call first', uid: 'held-call-resume-refused' }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    const record = held.record;
    store.dispatch(heldCallCleared(callId));
    setPersistentItem('vrs_active_call', JSON.stringify(held.record));
    callStartTimestamp = typeof record.startedAt === 'number' ? record.startedAt : Date.now();
    resetConferenceLifecycleState();
    lastPlatformCall = { callId: held.callId, roomName: held.roomName };
    startActiveCallReconciliationLoop(store);
    queueService.setCallHold(held.callId, false);
    mobileLog('info', 'vrs_held_call_resumed', {}, { console: false });
    dispatchRoomNavigation(store, {
        callId: held.callId,
        requestId: typeof record.requestId === 'string' ? record.requestId : undefined,
        roomName: held.roomName
    }, {
        hidePrejoin: true,
        startWithAudioMuted: false,
        startWithVideoMuted: getClientStartWithVideoMuted(record.clientStartWithVideoMuted)
    });
}
/**
 * From inside a call, back to the call this device holds (the in-call bar's
 * Swap, or End & go back). Robert, 2026-10-12: "yes other call should go on
 * hold". The current call is left as Hold & answer leaves it (no call_end,
 * its record kept for Resume) and the server puts it on hold; or, for a call
 * that may not be held, it ends. Then the held call resumes as from Home.
 *
 * @param {Object} store - The redux store.
 * @param {string} callId - The held call.
 * @param {string} mode - 'hold' (Swap) or 'end'.
 * @returns {void}
 */
function switchToHeldCallHere(store, callId, mode) {
    const held = store.getState()['features/call-management']?.heldCall;
    const current = getStoredActiveCall();
    if (!held || held.callId !== callId) {
        return;
    }
    if (!current?.callId || current.callId === callId) {
        resumeHeldCallHere(store, callId);
        return;
    }
    const holding = mode === 'hold';
    if (holding && !canHoldStoredCall(current)) {
        store.dispatch(showNotification({ title: 'A call with an interpreter cannot be put on hold.',
            uid: 'held-call-swap-refused' }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    const nextHeld = holding ? heldCallFromRecord(current) : null;
    if (holding) {
        queueService.setCallHold(current.callId, true);
        removePersistentItem('vrs_active_call');
    }
    else {
        writeLocalCallHistory();
        queueService.endActiveCall();
    }
    removePersistentItem('vrs_own_queue_request');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    forgetMediaRejoin();
    resetConferenceLifecycleState();
    mobileLog('info', 'vrs_held_call_switch', { mode }, { console: false });
    emitMobileTelemetryEvent('vrs_held_call_switch', { mode }, {
        callId: current.callId,
        roomName: current.roomName,
        state: holding ? 'held' : 'exiting'
    });
    store.dispatch(interpretedCallEnded(holding ? 'held_for_swap' : 'ended_for_held_call'));
    resumeHeldCallHere(store, callId);
    if (nextHeld) {
        store.dispatch(heldCallSet(nextHeld));
    }
}
/**
 * Ends the call this device holds, from the in-call bar (it is not this
 * device's current call, so the room it is in stays).
 *
 * @param {Object} store - The redux store.
 * @param {string} callId - The held call.
 * @returns {void}
 */
function endHeldCallHere(store, callId) {
    const held = store.getState()['features/call-management']?.heldCall;
    if (!held || held.callId !== callId) {
        return;
    }
    queueService.endCall(held.callId, held.roomName);
    store.dispatch(heldCallCleared(callId));
    mobileLog('info', 'vrs_held_call_ended_here', {}, { console: false });
}
// The longest wait for the room this device leaves, or for the server's
// answer to an end, before the new call goes out anyway.
const NEW_CALL_LEAVE_WAIT_MS = 3000;
/**
 * Hold and call, End and call, End it and call (activeCallGate offers).
 * Robert, 2026-10-13: "why can't we even make a second call again? Shouldn't
 * that just put the first call on hold?" The call this device is in goes on
 * hold (a direct or group call: no call_end, the server holds it, the in-call
 * bar and Home's Resume bring it back) or ends as a hang-up would. This
 * device leaves its room and goes Home, and only when the room is gone does
 * the screen's place() send the new call: the leave's navigation never races
 * the new room's. End it and call ends an open call this device is not in
 * (after a crash, or on another device) and waits for the server's answer,
 * so a call back to the same person rings as a call, not as a call waiting.
 *
 * @param {Object} store - The redux store.
 * @param {Object} action - START_CALL_AFTER_BUSY.
 * @returns {Promise<void>}
 */
async function startCallAfterBusy(store, action) {
    const place = typeof action.place === 'function' ? action.place : undefined;
    const openCall = isRecord(action.openCall) ? action.openCall : {};
    if (!place) {
        return;
    }
    if (action.offer === 'end_open_and_call') {
        const callId = stringValue(openCall.callId);
        if (callId) {
            await endOpenCallHere({ callId, roomName: stringValue(openCall.roomName) });
        }
        place();
        return;
    }
    const current = getStoredActiveCall();
    if (action.offer === 'hold_and_call' && current?.callId) {
        if (!holdCallForNewCall(store, { ...current, callId: current.callId })) {
            return;
        }
    }
    else if (action.offer === 'end_and_call' && current?.callId) {
        finalizeConferenceExit(store, 'ended_for_new_call', { navigate: false });
    }
    else {
        mobileLog('warn', 'vrs_new_call_after_busy_skipped', { hasCall: Boolean(current?.callId),
            offer: String(action.offer) }, { console: false });
        return;
    }
    store.dispatch(hangup(false));
    routeToRoleHome(current);
    await waitForConferenceGone(store);
    place();
}
/**
 * Hold and call: the call this device is in goes on hold as Swap holds it
 * (call_hold, its record kept for Resume, no call_end). Refused for a call
 * with an interpreter (holdPolicy.ts; the server refuses it too), and while
 * another call is held here (one at a time).
 *
 * @param {Object} store - The redux store.
 * @param {Object} current - The stored call.
 * @returns {boolean} Whether it went on hold.
 */
function holdCallForNewCall(store, current) {
    const held = store.getState()['features/call-management']?.heldCall;
    const holdable = canHoldStoredCall(current);
    if (!holdable || (held && held.callId !== current.callId)) {
        store.dispatch(showNotification({
            title: holdable ? 'Finish this call first' : 'A call with an interpreter cannot be put on hold.',
            uid: 'new-call-hold-refused'
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        mobileLog('warn', 'vrs_call_hold_for_new_call_refused', { reason: holdable ? 'call_held' : 'interpreted' }, { console: false });
        return false;
    }
    const nextHeld = heldCallFromRecord(current);
    queueService.setCallHold(current.callId, true);
    removePersistentItem('vrs_active_call');
    removePersistentItem('vrs_own_queue_request');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    forgetMediaRejoin();
    resetConferenceLifecycleState();
    mobileLog('info', 'vrs_call_held_for_new_call', {}, { console: false });
    emitMobileTelemetryEvent('vrs_call_held_for_new_call', {}, {
        callId: current.callId,
        roomName: current.roomName,
        state: 'held'
    });
    store.dispatch(interpretedCallEnded('held_for_new_call'));
    if (nextHeld) {
        store.dispatch(heldCallSet(nextHeld));
    }
    return true;
}
/**
 * Resolves once this device is in no conference (none, none joining, none
 * leaving), or after `timeoutMs`.
 *
 * @param {Object} store - The redux store.
 * @param {number} [timeoutMs] - The longest wait.
 * @returns {Promise<void>}
 */
function waitForConferenceGone(store, timeoutMs = NEW_CALL_LEAVE_WAIT_MS) {
    const gone = () => {
        const conference = store.getState()['features/base/conference'];
        return !conference?.conference && !conference?.joining && !conference?.leaving;
    };
    const started = Date.now();
    return new Promise(resolve => {
        const check = () => {
            if (gone() || Date.now() - started >= timeoutMs) {
                resolve();
                return;
            }
            setTimeout(check, 100);
        };
        check();
    });
}
/**
 * End it and call: ends an open call this device is not in, as this person's
 * hang-up would (call_end with confirmedEnd: the tap is the consent, also for
 * a call moved to another device of the account). Its record here is
 * cleared, and it never again blocks a call. Resolves on the server's answer
 * (call_ended, call_end_ignored, an error), or after NEW_CALL_LEAVE_WAIT_MS.
 *
 * @param {Object} openCall - The server's open call.
 * @returns {Promise<void>}
 */
function endOpenCallHere(openCall) {
    const stored = getStoredActiveCall();
    noteCallEndedHere(openCall);
    if (stored && (stored.callId === openCall.callId
        || (openCall.roomName && stored.roomName === openCall.roomName))) {
        removePersistentItem('vrs_active_call');
        stopActiveCallReconciliationLoop();
        forgetMediaRejoin();
        resetConferenceLifecycleState();
    }
    mobileLog('info', 'vrs_open_call_end_requested', { stored: Boolean(stored) }, { console: false });
    return new Promise(resolve => {
        let timer;
        const onEnded = (data) => {
            if (data?.callId === openCall.callId) {
                finish('ended');
            }
        };
        const onIgnored = (data) => {
            if (!data?.callId || data.callId === openCall.callId) {
                finish('ignored');
            }
        };
        const onError = () => finish('error');
        const finish = (outcome) => {
            if (!timer) {
                return;
            }
            clearTimeout(timer);
            timer = undefined;
            queueService.off('callEnded', onEnded);
            queueService.off('callEndIgnored', onIgnored);
            queueService.off('error', onError);
            mobileLog('info', 'vrs_open_call_end_answered', { outcome }, { console: false });
            resolve();
        };
        timer = setTimeout(() => finish('timeout'), NEW_CALL_LEAVE_WAIT_MS);
        queueService.on('callEnded', onEnded);
        queueService.on('callEndIgnored', onIgnored);
        queueService.on('error', onError);
        queueService.endOpenCall(openCall.callId, openCall.roomName);
    });
}
/**
 * Merge (Robert, 2026-10-12): the call this device holds joins the one it is
 * in. P2P and group calls only; the server checks the same, the 8-person cap
 * and blocks (vrs-server/ws/call-management-handlers.js handleCallMerge).
 *
 * @param {Object} store - The redux store.
 * @param {string} callId - The held call.
 * @returns {void}
 */
function mergeHeldCallHere(store, callId) {
    const held = store.getState()['features/call-management']?.heldCall;
    const current = getStoredActiveCall();
    if (!held || held.callId !== callId || !current?.callId || current.callId === callId) {
        return;
    }
    if (!canHoldStoredCall(current) || !canHoldStoredCall(held.record)) {
        store.dispatch(showNotification({
            title: 'A call with an interpreter cannot be merged.',
            uid: 'held-call-merge-refused'
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    queueService.mergeHeldCall(held.callId, current.callId);
    mobileLog('info', 'vrs_held_call_merge_requested', {}, { console: false });
}
/**
 * This device was in a call on hold that was merged into another
 * (call_merged): it leaves that room without ending anything (the server
 * ends the held call) and enters the merged call's room.
 *
 * @param {Object} store - The redux store.
 * @param {Object} data - The merged call.
 * @returns {void}
 */
function enterMergedCall(store, data) {
    const stored = getStoredActiveCall();
    // Only the device in the merged-away call moves.
    if (!stored?.callId || stored.callId !== data.fromCallId) {
        return;
    }
    removePersistentItem('vrs_active_call');
    removePersistentItem('vrs_own_queue_request');
    lastAutoEnteredQueueRoom = undefined;
    stopActiveCallReconciliationLoop();
    forgetMediaRejoin();
    resetConferenceLifecycleState();
    store.dispatch(interpretedCallEnded('merged'));
    store.dispatch(callOffHold({
        callId: data.fromCallId,
        heldBy: ''
    }));
    store.dispatch(hideNotification(`call-hold-${data.fromCallId}`));
    mobileLog('info', 'vrs_call_merged_here', {}, { console: false });
    emitMobileTelemetryEvent('vrs_call_merged_here', {}, {
        callId: data.callId,
        roomName: data.roomName,
        state: 'entering'
    });
    markPlatformCallGroup(data.callId);
    recordPlatformCallHost(data.callId, data.hostId || undefined);
    enterPlatformCallRoom(store, {
        callId: data.callId,
        clientId: data.hostId || undefined,
        groupCall: true,
        hostId: data.hostId || undefined,
        roomName: data.roomName
    });
    store.dispatch(showNotification({
        title: data.mergedBy ? `${data.mergedBy} merged the calls` : 'Calls merged',
        uid: `call-merged-${data.callId}`
    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
}
let heldSessionWatch;
/**
 * One live call per account across devices (vrs-server/lib/call-holders.js):
 * the notices this device shows when the server holds it out, supersedes it,
 * or moves the client's call to another of the account's devices.
 */
function installAccountDeviceNotices(store) {
    bindAccountDeviceDispatch(store.dispatch);
    // Nothing listened for this before, so a superseded room page just went
    // quiet (the demo incident). Say so, and offer to switch back.
    queueService.on('sessionSuperseded', data => {
        if (!isAccountDeviceRoomPage()) {
            return;
        }
        const { reload } = accountDevicePageActions();
        const sameBrowser = data.sameBrowser === true;
        presentAccountDeviceNotice({ kind: 'superseded', message: data.message, sameBrowser }, reload && !sameBrowser ? { useThisDevice: reload } : {});
    });
    queueService.on('sessionHeldElsewhere', hold => {
        heldSessionWatch?.stop();
        heldSessionWatch = undefined;
        if (!isAccountDeviceRoomPage()) {
            return;
        }
        heldSessionWatch = watchHeldSession({
            dismiss: dismissAccountDeviceNotice,
            goHome: routeToRoleHome,
            hold,
            present: presentAccountDeviceNotice,
            service: {
                off: (event, callback) => queueService.off(event, callback),
                on: (event, callback) => queueService.on(event, callback),
                requestAccountCallStatus: () => queueService.requestAccountCallStatus(),
                retakeSession: () => queueService.retakeSession()
            }
        });
    });
    queueService.on('authenticated', () => {
        heldSessionWatch?.stop();
        heldSessionWatch = undefined;
        dismissAccountDeviceNotice('held');
    });
    queueService.on('callMovingElsewhere', data => {
        if (callMoveBelongsHere(data)) {
            presentAccountDeviceNotice(moveNotice('moving', data), {});
        }
    });
    queueService.on('callMoveLapsed', data => {
        if (callMoveBelongsHere(data)) {
            presentAccountDeviceNotice(moveNotice('lapsed', data), {});
        }
    });
    queueService.on('callMovedElsewhere', data => leaveMovedCall(store, data));
    queueService.on('callEndIgnored', data => {
        mobileLog('info', 'vrs_call_end_ignored', { callId: data.callId, reason: data.reason }, { console: false });
    });
    // The app's "In a call on another device" with "Move call here" (VRS
    // Home, VRI console); the web portal draws its own card
    // (client-profile.js). The server says so on sign-in and at the match;
    // the answer to account_call_status keeps it current.
    const appClient = () => isNativeRuntime() && getUserRole() === 'client';
    queueService.on('callActiveElsewhere', data => {
        mobileLog('info', 'vrs_call_active_elsewhere', { callId: data.callId }, { console: false });
        if (appClient() && data.canMoveHere !== false) {
            setCallElsewhere(store, data);
        }
    });
    queueService.on('accountCallStatus', status => {
        if (status.role !== 'client' || !appClient()) {
            return;
        }
        setCallElsewhere(store, status.held ? status : undefined);
    });
    // Signed in again: ask, so a row from before the drop does not linger.
    queueService.on('authenticated', () => {
        if (appClient() && store.getState()['features/interpreter-queue']?.callElsewhere) {
            queueService.requestAccountCallStatus();
        }
    });
}
function recordConferenceJoinFailure(action, event) {
    const activeCall = getStoredActiveCall();
    if (!activeCall || conferenceJoinedForActiveCall) {
        return;
    }
    conferenceJoinFailure = { event, ...conferenceErrorDetails(action.error) };
    mobileLog('error', 'vrs_conference_join_failed', { ...conferenceJoinFailure }, { console: false });
    emitMobileTelemetryEvent('vrs_conference_join_failed', { ...conferenceJoinFailure }, {
        callId: activeCall.callId,
        requestId: activeCall.requestId,
        roomName: activeCall.roomName,
        state: 'join_failed'
    });
}
function preserveMatchedCallBeforeJoin(reason) {
    const activeCall = getStoredActiveCall();
    const exitKey = getActiveCallExitKey(activeCall);
    if (!exitKey || preJoinExitHandledKey === exitKey) {
        return;
    }
    preJoinExitHandledKey = exitKey;
    emitMobileTelemetryEvent('vrs_prejoin_exit_preserved', {
        failure: conferenceJoinFailure,
        reason
    }, {
        callId: activeCall?.callId,
        requestId: activeCall?.requestId,
        roomName: activeCall?.roomName,
        state: 'retryable'
    });
}
/**
 * Whether the stored call is a group call (its own leave-and-rejoin rules).
 *
 * @param {StoredActiveCall} activeCall - The stored call.
 * @returns {boolean}
 */
function isGroupCallRecord(activeCall) {
    return activeCall.groupCall === true || Boolean(activeCall.roomName?.startsWith('group-'));
}
/**
 * The room's latest camera change: a camera track added, or a camera turned
 * back on (TRACK_UPDATED carrying muted: false). Screen shares do not count.
 *
 * @param {Object} action - A TRACK_ADDED or TRACK_UPDATED action.
 * @returns {void}
 */
function noteVideoChange(action) {
    const track = action.track;
    if (!track || track.videoType === 'desktop') {
        return;
    }
    const mediaType = track.mediaType ?? track.jitsiTrack?.getType?.();
    if (mediaType !== 'video') {
        return;
    }
    const side = (track.local ?? track.jitsiTrack?.isLocal?.()) === true ? 'local' : 'remote';
    if (action.type === TRACK_ADDED) {
        lastVideoChange = {
            at: Date.now(),
            kind: `${side}_camera_added`
        };
    }
    else if (track.muted === false) {
        lastVideoChange = {
            at: Date.now(),
            kind: `${side}_camera_on`
        };
    }
}
/**
 * The joined call's conference failed. A media-session failure (the set
 * jitsi-meet answers with "reload and rejoin") is recorded so the exit that
 * follows keeps the call; every in-call failure is reported, with the camera
 * change that preceded it.
 *
 * Runs BEFORE the reducer sees the error: the reload overlay (web) and dialog
 * (native) read the rejoin marker for their countdown as they mount.
 *
 * @param {Object} action - The CONFERENCE_FAILED action.
 * @returns {void}
 */
function noteInCallConferenceFailure(action) {
    const activeCall = getStoredActiveCall();
    const error = action.error;
    if (!activeCall?.callId || !activeCall.roomName || !conferenceJoinedForActiveCall
        || !action.conference || action.conference !== joinedConferenceForActiveCall
        || !isConferenceForActiveRoom(activeCall.roomName, action.conference)) {
        return;
    }
    const now = Date.now();
    const errorName = typeof error?.name === 'string' ? error.name.slice(0, 120) : 'unknown';
    const mediaSession = isMediaSessionFailure(errorName);
    const groupCall = isGroupCallRecord(activeCall);
    const marker = mediaSession && !groupCall
        ? recordMediaFailure(readMediaRejoinMarker(now), {
            callId: activeCall.callId,
            roomName: activeCall.roomName
        }, errorName, now)
        : undefined;
    if (marker) {
        writeMediaRejoinMarker(marker);
        inCallMediaFailure = {
            callId: activeCall.callId,
            conference: action.conference,
            rejoin: marker.pending
        };
    }
    let outcome = 'not_media_session';
    if (mediaSession) {
        outcome = groupCall ? 'group_call' : marker?.pending ? 'rejoin' : 'rejoin_limit_reached';
    }
    const details = {
        attempt: marker?.attempts,
        error: errorName,
        message: mediaFailureMessage(error?.params),
        msSinceJoin: activeConferenceJoinedAt === undefined ? undefined : Math.max(0, now - activeConferenceJoinedAt),
        msSinceVideoChange: lastVideoChange ? Math.max(0, now - lastVideoChange.at) : undefined,
        outcome,
        role: getUserRole(),
        videoChange: lastVideoChange?.kind
    };
    mobileLog('warn', 'vrs_in_call_conference_failed', details, { console: false });
    emitMobileTelemetryEvent('vrs_in_call_conference_failed', details, {
        callId: activeCall.callId,
        // The web page reloads seconds later: send now, not with the batch.
        keepalive: true,
        requestId: activeCall.requestId,
        roomName: activeCall.roomName,
        state: outcome
    });
}
/**
 * The failed conference is leaving. The call stays: no call_end, the stored
 * call is kept, and the page is not sent home. The reload overlay (web) or
 * dialog (native) of jitsi-meet rejoins the same room.
 *
 * @param {string} reason - The exit being kept.
 * @returns {void}
 */
function keepCallForMediaRejoin(reason) {
    const failure = inCallMediaFailure;
    if (!failure || failure.kept) {
        return;
    }
    failure.kept = true;
    const activeCall = getStoredActiveCall();
    emitMobileTelemetryEvent('vrs_in_call_rejoin_pending', {
        reason,
        reloadSeconds: MEDIA_REJOIN_RELOAD_SECONDS
    }, {
        callId: failure.callId,
        keepalive: true,
        requestId: activeCall?.requestId,
        roomName: activeCall?.roomName,
        state: 'rejoining'
    });
}
/**
 * Back in the call's room after a media-session failure: report how long the
 * rejoin took, and keep the attempt count for the budget.
 *
 * @param {StoredActiveCall} activeCall - The stored call.
 * @returns {void}
 */
function completeMediaRejoin(activeCall) {
    inCallMediaFailure = undefined;
    const now = Date.now();
    const marker = readMediaRejoinMarker(now);
    if (!marker?.pending || !activeCall?.callId || marker.callId !== activeCall.callId) {
        return;
    }
    writeMediaRejoinMarker({
        ...marker,
        pending: false
    });
    emitMobileTelemetryEvent('vrs_in_call_rejoin_succeeded', {
        attempt: marker.attempts,
        error: marker.error,
        rejoinMs: Math.max(0, now - marker.lastFailedAt)
    }, {
        callId: activeCall.callId,
        requestId: activeCall.requestId,
        roomName: activeCall.roomName,
        state: 'rejoined'
    });
}
/**
 * The call is over here: nothing is pending a rejoin any more.
 *
 * @returns {void}
 */
function forgetMediaRejoin() {
    inCallMediaFailure = undefined;
    if (readMediaRejoinMarker()) {
        writeMediaRejoinMarker(null);
    }
}
/**
 * Whether the stored call is held when the page goes away: a one-to-one call,
 * not a group. Either an interpreted one (a queue call, VRS or VRI, or one
 * with its interpreter recorded: the reconnect grace,
 * vrs-server/ws/call-reconnect-grace.js), or a direct call in its `p2p-` room,
 * an instant meeting's included (once answered, the server's disconnect
 * guard gives it the same window: holdDirectCallOnPageExit in
 * vrs-server/ws/p2p-handlers.js; a call still ringing it ends as before).
 * Any other call ends as before.
 *
 * @param {StoredActiveCall} activeCall - The stored call.
 * @returns {boolean}
 */
function isHeldCallRecord(activeCall) {
    const callType = activeCall?.callType;
    return Boolean(activeCall?.callId && !isGroupCallRecord(activeCall)
        && (callType === 'vrs' || callType === 'vri' || activeCall.interpreterId
            || activeCall.roomName?.startsWith('p2p-')));
}
// The call this page told the server it is leaving without hanging up
// (call_end, departure 'page_exit').
let callAwaySentFor;
let pageLeaveListenerInstalled = false;
// How long after a beforeunload a page still running is taken to have stayed.
const PAGE_STAYED_CHECK_MS = 3000;
/**
 * This page is going away (reload, closed tab, navigating off) during the
 * stored call. For a one-to-one call, interpreted or direct (isHeldCallRecord),
 * that is not hanging up: the server holds the call for this person to come
 * back (call_end with departure 'page_exit', the reconnect grace), a reload
 * rejoins it by itself, and for an interpreted call the portal offers the way
 * back. The stored call stays for the page that comes back. Client from the
 * Pixel session's call-rejoin-grace (e32504d22).
 * With the grace turned off on the server, leaving ends the call as before.
 *
 * @param {string} reason - What reported the page leaving.
 * @returns {boolean} Whether the call is kept (no call_end).
 */
function keepCallForPageLeave(reason) {
    const activeCall = getStoredActiveCall();
    if (!isNativeRuntime() && activeCall?.callId && callAwaySentFor === activeCall.callId) {
        return true;
    }
    if (isNativeRuntime() || !isHeldCallRecord(activeCall) || !activeCall?.callId || reconnectGraceMs === 0) {
        return false;
    }
    const joinedHere = conferenceJoinedForActiveCall
        || isMediaRejoinPending(readMediaRejoinMarker(), activeCall.callId, Date.now());
    if (!joinedHere) {
        return false;
    }
    const sent = queueService.notifyPageExit(activeCall.callId, activeCall.roomName);
    callAwaySentFor = activeCall.callId;
    emitMobileTelemetryEvent('vrs_call_page_left', {
        reason,
        sent
    }, {
        callId: activeCall.callId,
        keepalive: true,
        requestId: activeCall.requestId,
        roomName: activeCall.roomName,
        state: 'away'
    });
    // A beforeunload can be answered with "Stay": if this page is still here
    // in a moment, it never left, so the call is not held any more. (Timers
    // do not run once the page has gone.)
    if (reason === 'beforeunload') {
        const callId = activeCall.callId;
        setTimeout(() => {
            if (callAwaySentFor !== callId) {
                return;
            }
            callAwaySentFor = undefined;
            queueService.notifyCallRoomJoined(callId);
            emitMobileTelemetryEvent('vrs_call_page_stayed', {}, {
                callId,
                state: 'back'
            });
        }, PAGE_STAYED_CHECK_MS);
    }
    return true;
}
/**
 * A client's room page going away (web) in a way keepCallForPageLeave did not
 * keep: before its conference joined, or a call that is not held. Until
 * 2026-09-25 the room pill (vrs-room-controls.js) sent this over a second
 * socket; the rules are the pill's.
 *
 * - Only a client, only the stored call of this page's room, and never a call
 *   that moved to another of the account's devices. Interpreters are left
 *   out on purpose: their room page re-navigates for ordinary reasons, and
 *   when the pill listened for every role each of those hung up a live call.
 * - A one-to-one call, interpreted or direct (isHeldCallRecord), or one mid
 *   media rejoin, is held: page_exit goes out and the stored call stays for
 *   the page that comes back. With the grace off, a media rejoin still keeps
 *   the call and sends nothing.
 * - Any other call: page_exit (the server decides), the group reload marker,
 *   and the stored call is forgotten.
 * - Not from a prerendered room the portal never showed.
 *
 * @returns {void}
 */
export function leaveClientCallOnPageHide() {
    // A manager's hidden observation is never the saved client's page (the
    // middleware returns early there too; the pill checked this itself).
    if (isObserverRoomPage() || isNativeRuntime() || isRoomActivationHeld() || getUserRole() !== 'client') {
        return;
    }
    const call = getStoredActiveCall();
    if (!call?.callId || !call.roomName || call.roomName !== getCurrentBrowserRoomName()
        || queueService.hasCallMovedAway(call.callId)) {
        return;
    }
    if (reconnectGraceMs !== 0 && isHeldCallRecord(call)) {
        queueService.notifyPageExit(call.callId, call.roomName);
        return;
    }
    if (isMediaRejoinPending(readMediaRejoinMarker(), call.callId, Date.now())) {
        if (reconnectGraceMs !== 0) {
            queueService.notifyPageExit(call.callId, call.roomName);
        }
        return;
    }
    rememberGroupRejoin(call);
    queueService.endActiveCall('page_exit');
}
/**
 * Web: a page leaving without its conference's beforeunload (that handler is
 * removed once the conference fails, and some browsers skip beforeunload)
 * still tells the server it is away rather than hanging up; a client's page
 * that leaves before joining, or leaves a call that is not held, ends it
 * (leaveClientCallOnPageHide).
 *
 * @returns {void}
 */
function installPageLeaveListener() {
    if (pageLeaveListenerInstalled || isNativeRuntime()
        || typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
        return;
    }
    pageLeaveListenerInstalled = true;
    window.addEventListener('pagehide', () => {
        // The call page is going: Home may take the session back at once.
        stopRoomTabPresence();
        if (hasStoredActiveCall() && !keepCallForPageLeave('pagehide')) {
            leaveClientCallOnPageHide();
        }
    });
}
/**
 * Tell the server this device's room for the stored call is up (call_room_entered),
 * which brings its person back from a reconnect grace at once: the page of
 * the call's room has loaded and signed in (web), or the app is in, or on its
 * way back into, the call's conference (native). Media can take many seconds
 * more; it is reported as call_room_joined. A group call's rejoin is its own
 * (groupRejoin.ts), and a portal page or another room never says so.
 *
 * @param {string} reason - What prompted it (telemetry).
 * @param {boolean} [enteringNow] - Native: the app is navigating into the room now.
 * @returns {void}
 */
function announceRoomEntered(reason, enteringNow = false) {
    const activeCall = getStoredActiveCall();
    const role = getUserRole();
    if (!activeCall?.callId || !activeCall.roomName || isGroupCallRecord(activeCall)
        || (role !== 'client' && role !== 'interpreter')) {
        return;
    }
    const inRoom = isNativeRuntime()
        ? enteringNow || conferenceJoinedForActiveCall
        : getBackendSafeRoomName(getCurrentBrowserRoomName()) === getBackendSafeRoomName(activeCall.roomName);
    if (!inRoom) {
        return;
    }
    queueService.notifyCallRoomEntered(activeCall.callId);
    if (roomEnteredSentFor !== activeCall.callId) {
        roomEnteredSentFor = activeCall.callId;
        mobileLog('info', 'vrs_call_room_entered', { reason }, { console: false });
    }
}
/**
 * Native: this person's call is held for them (call_reconnect_available: the
 * app was closed or lost its connection, and has just signed in again). If
 * the app is not in a conference, it goes back into the call's room within
 * the grace. The web room page returns by reloading; its portal offers the
 * way back itself.
 *
 * @param {Object} store - The redux store.
 * @param {Object} data - The offer.
 * @returns {void}
 */
function reenterHeldCall(store, data) {
    const role = getUserRole();
    if (!isNativeRuntime() || !data.callId || !data.roomName || (data.role && data.role !== role)) {
        return;
    }
    if (conferenceJoinedForActiveCall || store.getState()?.['features/base/conference']?.conference) {
        return;
    }
    const stored = getStoredActiveCall();
    if (stored?.callId && stored.callId !== data.callId) {
        return;
    }
    if (!stored?.callId) {
        setPersistentItem('vrs_active_call', JSON.stringify({
            callId: data.callId,
            callType: data.callType || undefined,
            role,
            roomName: data.roomName,
            startedAt: Date.now()
        }));
    }
    const call = { ...(stored || {}), callId: data.callId, roomName: data.roomName };
    emitMobileTelemetryEvent('vrs_call_reconnect_reenter', {
        role,
        secondsLeft: data.secondsLeft
    }, {
        callId: data.callId,
        roomName: data.roomName,
        state: 'rejoining'
    });
    startActiveCallReconciliationLoop(store);
    dispatchRoomNavigation(store, call, {
        hidePrejoin: true,
        startWithAudioMuted: false,
        startWithVideoMuted: role === 'client' ? shouldStartClientVideoMuted(store, call) : false
    });
    announceRoomEntered('reenter', true);
}
/**
 * Returns the matched queue room name when present.
 *
 * @param {Object} data - The queue match payload.
 * @returns {string|undefined}
 */
function getQueueRoomName(data) {
    return data.roomName?.trim()
        ? data.roomName
        : undefined;
}
/**
 * Returns a stable navigation key for a queue match event.
 *
 * @param {Object} data - The queue match payload.
 * @param {string} roomName - The matched room name.
 * @returns {string}
 */
function getQueueNavigationKey(data, roomName) {
    return data.callId || data.requestId || roomName;
}
/**
 * Opens the call's room. `source` names what opened it in the join marker:
 * 'auto_enter' for a queue match (the metrics take it as the match itself),
 * or a direct call's own moment ('caller_ringing', 'answer', ...), so a
 * direct call's connect time is measured from the answer
 * (vrs-server/lib/metrics.js answer_to_first_frame).
 */
function dispatchRoomNavigation(store, data, options, source = 'auto_enter') {
    rememberJoinStart(data, getCurrentBrowserRoomName() ? 'in_room_navigate' : source);
    const reportFailure = (error) => {
        const details = conferenceErrorDetails(error);
        mobileLog('error', 'queue_room_navigation_failed', details, { console: false });
        emitMobileTelemetryEvent('queue_room_navigation_failed', details, {
            callId: data.callId,
            requestId: data.requestId,
            roomName: data.roomName,
            state: 'join_failed'
        });
    };
    try {
        const result = store.dispatch(appNavigate(getRoomNavigationTarget(data.roomName || ''), options));
        const catchResult = result && typeof result.catch === 'function'
            ? result.catch(reportFailure)
            : undefined;
        void catchResult;
    }
    catch (error) {
        reportFailure(error);
    }
}
/**
 * The room this app is in now. The web reads the page address. A phone has
 * no page address (its location polyfill has no pathname), so it reads the
 * conference room. Robert, 2026-10-05: on the phone a replayed p2p_join_room
 * entered the same room again and rewrote the call record without the
 * caller's clientId, so the interpreter button left the toolbar of a group call.
 */
function getCurrentRoomName(store) {
    if (isNativeRuntime()) {
        const room = store.getState?.()['features/base/conference']?.room;
        return typeof room === 'string' && room ? room : undefined;
    }
    return getCurrentBrowserRoomName();
}
function getCurrentBrowserRoomName() {
    const pathname = typeof window !== 'undefined' ? window.location?.pathname : undefined;
    if (!pathname) {
        return undefined;
    }
    const parts = pathname.split('/').filter(Boolean);
    const roomName = parts[parts.length - 1];
    return roomName && !roomName.endsWith('.html') ? decodeURIComponent(roomName) : undefined;
}
function toMatchData(data) {
    const roomName = data.roomName || '';
    return {
        callerIdPhone: data.callerIdPhone,
        callId: data.callId || data.requestId || roomName,
        callType: data.callType,
        clientId: data.clientId || '',
        clientName: data.clientName || 'Client',
        clientStartWithVideoMuted: data.clientStartWithVideoMuted,
        interpreterId: data.interpreterId || '',
        interpreterName: data.interpreterName || 'Interpreter',
        language: data.language || 'ASL',
        requestKind: data.requestKind,
        requestId: data.requestId || data.callId || roomName,
        roomName,
        targetPhone: data.targetPhone,
        targetPhoneLast4: data.targetPhoneLast4,
        vcoEnabled: data.vcoEnabled
    };
}
/**
 * The client's own Zoom/Meet/Teams/Webex meeting named by a payload, if any
 * ("Interpret my meeting", vrs-server/lib/external-meeting-vrs.js).
 */
/** Take one offer card down (a transfer's decline was acknowledged or refused). */
function removeOfferCard(store, cardId) {
    forgetTransferAction(cardId);
    store.dispatch({ type: REQUEST_DECLINED, requestId: cardId });
}
/** The transfer this interpreter accepted has put them in its call. */
function clearTransferCardsForCall(store, callId) {
    if (!callId || typeof store.getState !== 'function') {
        return;
    }
    const pending = store.getState()?.['features/interpreter-queue']?.pendingRequests || [];
    pending.filter(request => request.transfer?.callId === callId)
        .forEach(request => removeOfferCard(store, request.id));
}
function toExternalMeeting(value) {
    return value?.platform ? { host: value.host, platform: value.platform, url: value.url } : undefined;
}
function toInterpreterRequest(data) {
    return {
        externalMeeting: toExternalMeeting(data.externalMeeting),
        callerIdPhone: data.callerIdPhone,
        callType: data.callType,
        clientName: data.clientName,
        corporateAccountId: data.corporateAccountId,
        costCenter: data.costCenter,
        department: data.department,
        id: data.id,
        industryTag: data.industryTag,
        instructions: data.instructions,
        language: data.language,
        offerId: data.offerId,
        offerExpiresAt: data.offerExpiresAt,
        requestKind: data.requestKind,
        roomName: data.roomName,
        scheduledSessionId: data.scheduledSessionId,
        scheduledStartAt: data.scheduledStartAt,
        targetPhone: data.targetPhone,
        targetPhoneLast4: data.targetPhoneLast4,
        timestamp: data.timestamp || Date.now(),
        vcoEnabled: data.vcoEnabled,
        demoMode: data.demoMode === true
    };
}
function applyProfileDisplayName(store) {
    // Without this the other side's name pill falls back to the role word
    // ("Client") — carry the signed-in profile name into the conference. An
    // interpreter or captioner carries their ID instead ("Interpreter #1042",
    // providerDisplayName.ts): the people they serve never see their name.
    const user = getPersistentJson('vrs_user_info');
    const role = getUserRole();
    const name = role === 'interpreter' || role === 'captioner'
        ? roomDisplayName(user, role)
        : (user?.name || '').trim();
    if (name) {
        store.dispatch(updateSettings({ displayName: name }));
    }
}
function shouldStartClientVideoMuted(store, data) {
    if (typeof data?.clientStartWithVideoMuted === 'boolean') {
        return data.clientStartWithVideoMuted;
    }
    const serverPreference = store.getState()?.['features/call-management']?.preferences?.camera_default_off;
    if (typeof serverPreference === 'boolean') {
        return serverPreference;
    }
    return getClientStartWithVideoMuted();
}
/**
 * Matches are delivered to every signed-in device of the account (so pushes
 * can wake sleeping phones). Only the device that placed the request — or is
 * already on this very call — may silently auto-enter. Any other device
 * rings instead: without this, a logged-in phone and laptop take turns
 * superseding each other's room session and the camera strobes on/off.
 */
function isMatchForThisDevice(data) {
    const stored = getStoredActiveCall();
    const own = getPersistentJson('vrs_own_queue_request');
    // Interpreters bypass the own-request marker: the server confirms an
    // accept only to the socket that sent it, so an interpreter match is
    // always this device's accept — gating it here silently swallowed the
    // terp accept→room navigation. Clients keep the f22ef958 rule.
    return shouldAutoEnterMatchedRoom({
        matchCallId: data.callId,
        ownRequestAt: own?.at,
        role: getUserRole(),
        storedCallId: stored?.callId
    });
}
function autoEnterQueueRoom(store, data, options) {
    const roomName = getQueueRoomName(data);
    if (!roomName) {
        return;
    }
    if (!isMatchForThisDevice(data)) {
        emitMobileTelemetryEvent('queue_match_other_device', {}, {
            callId: data.callId,
            requestId: data.requestId,
            roomName,
            state: 'ringing'
        });
        // Web gets the deaf-accessible ring overlay; native devices are rung
        // by the CallKit/FCM push for this same match.
        const isNative = typeof navigator !== 'undefined'
            && navigator.product === 'ReactNative';
        if (!isNative && data.callId) {
            store.dispatch(incomingP2PCall({
                callId: data.callId,
                roomName,
                callerId: '',
                // Named by the same rule the CallKit/Telecom ring uses, so
                // the same match is not one thing on the phone and another in
                // the browser. Never the interpreter's name.
                callerName: getCallPeerLabel(data, getUserRole()) || 'Your interpreter is ready',
                clientStartWithVideoMuted: data.clientStartWithVideoMuted,
                hasVideo: true,
                groupCall: false,
                inboundVrs: true
            }));
        }
        return;
    }
    applyProfileDisplayName(store);
    const navigationKey = getQueueNavigationKey(data, roomName);
    if (lastAutoEnteredQueueRoom === navigationKey) {
        return;
    }
    lastAutoEnteredQueueRoom = navigationKey;
    // Persist match metadata for CDR/call history on call end
    persistMatchData(data);
    startActiveCallReconciliationLoop(store);
    if (getCurrentRoomName(store) === roomName) {
        return;
    }
    queueRoomNavigationStartedAt = Date.now();
    queueRoomSignalingConnectedAt = undefined;
    queueRoomMediaConnectedAt = undefined;
    queueRoomRemoteVideoAt = undefined;
    emitMobileTelemetryEvent('queue_room_auto_enter', {
        startWithAudioMuted: options.startWithAudioMuted,
        startWithVideoMuted: options.startWithVideoMuted
    }, {
        callId: data.callId,
        requestId: data.requestId,
        roomName,
        state: 'joining'
    });
    // Queue joins bypass prejoin/lobby screens; media defaults differ by role.
    // Navigate to an absolute tenant URL on native so the self-hosted bridge
    // is used instead of the public Jitsi (meet.jit.si) default.
    dispatchRoomNavigation(store, data, {
        hidePrejoin: true,
        startWithAudioMuted: options.startWithAudioMuted,
        startWithVideoMuted: options.startWithVideoMuted
    });
}
function enterPlatformCallRoom(store, data, peerName, source = 'answer') {
    if (!data.roomName) {
        return;
    }
    // p2p_join_room / p2p_ringing can be replayed (reconnect delivery, a
    // sibling instance re-publishing). Same call, same room, already there:
    // nothing to do — the queue path has had this guard for a while.
    if (lastPlatformCall
        && lastPlatformCall.roomName === data.roomName
        && (lastPlatformCall.callId || '') === (data.callId || '')
        && getCurrentRoomName(store) === data.roomName) {
        return;
    }
    lastPlatformCall = { callId: data.callId, roomName: data.roomName };
    // A reloaded group room asked to be seated again (groupRejoin.ts): this
    // p2p_join_room is the answer, and the page is already on its way in.
    const rejoiningHere = isGroupRejoinPending(data.callId) && getCurrentRoomName(store) === data.roomName;
    applyProfileDisplayName(store);
    persistMatchData({
        callId: data.callId,
        callType: data.callType,
        // Who placed the call (calls.client_id): only they are offered the
        // room's interpreter button (p2pInterpreterEligibility). In a group
        // call, ending it for everyone is the host's (hostId; until the
        // server names one, the caller: groupEndForAll.ts).
        clientId: data.clientId,
        groupCall: data.groupCall,
        hostId: data.hostId,
        interpreterId: data.interpreterId,
        interpreterName: data.interpreterName,
        clientStartWithVideoMuted: data.clientStartWithVideoMuted,
        roomName: data.roomName,
        clientName: [peerName, data.calleeName, data.callerName].map(usablePlatformPeerName).find(Boolean)
            || (data.groupCall ? 'Group call' : 'Direct call'),
        language: 'ASL',
        // The person rung (your own call) or the one ringing you; a group has
        // no one face (profilePhoto.ts).
        peerPhotoUrl: data.groupCall ? undefined : profilePhotoPath(data.calleePhotoUrl)
            || profilePhotoPath(data.callerPhotoUrl) || rememberedCallPhoto(data.callId)
    });
    startActiveCallReconciliationLoop(store);
    emitMobileTelemetryEvent('platform_call_room_enter', {
        groupCall: Boolean(data.groupCall),
        participantCount: data.participantCount
    }, {
        callId: data.callId,
        requestId: data.requestId,
        roomName: data.roomName,
        state: 'joining'
    });
    if (rejoiningHere) {
        return;
    }
    dispatchRoomNavigation(store, data, {
        hidePrejoin: true,
        // Starting or accepting the platform call is the consent boundary;
        // no media is acquired while Home or the incoming sheet is idle.
        startWithAudioMuted: false,
        startWithVideoMuted: data.inboundVrs === true
            ? shouldStartClientVideoMuted(store, data)
            : false
    }, source);
}
function hasInterpreterSessionToken() {
    return Boolean(getSecureItem('vrs_auth_token'));
}
function getDesktopBridge() {
    return typeof window !== 'undefined'
        ? window.MalkaDesktop
        : undefined;
}
function notifyDesktopOfQueue(action, getState) {
    const bridge = getDesktopBridge();
    if (!bridge) {
        return;
    }
    if (action.type === INTERPRETER_REQUEST_RECEIVED && action.request) {
        const request = action.request;
        bridge.incomingCall({
            callType: request.callType || 'vrs',
            clientName: request.clientName || 'Client',
            language: request.language || 'ASL',
            requestKind: request.requestKind || 'standard',
            targetPhone: request.targetPhone
        });
        return;
    }
    // Any resolution — accepted, declined, matched — clears the alert once
    // no requests remain pending.
    if (action.type === REQUEST_ACCEPTED || action.type === REQUEST_DECLINED || action.type === MATCH_FOUND) {
        const pending = getState()?.['features/interpreter-queue']?.pendingRequests || [];
        if (action.type === MATCH_FOUND || pending.length === 0) {
            bridge.clearIncomingCall();
        }
    }
}
// Module-scoped rather than a store property: sign-out paths must be able to
// reset it without reaching into the store (see resetInterpreterQueueMiddleware).
let _interpreterQueueMiddlewareInitialized = false;
// The store the listeners were wired to, so sign-out can clear what they set.
let interpreterQueueStore;
// The queue listeners that the last setup added, and the role of that setup.
// The first action sets the queue up. In the apps, that action comes before
// the sign-in, or before the app reads the stored session, so the role is the
// default, 'client'. Malka Terp then got no offers, and "Go available" was
// never confirmed, until a sign-out and a new sign-in (design review,
// 2026-10-13). With this list, a new role replaces exactly this setup.
// queueService.disconnect() is not an alternative: it removes the listeners
// of all modules.
let setupQueueListeners = [];
let setupRole;
// The one listener of a refused session refresh (api-client onAuthSessionEnded):
// each setup replaces it, so a new role never adds a second one.
let stopAuthSessionEndedListener;
/**
 * Sets the queue up for the stored role, and keeps each queue listener that
 * the setup adds, so that releaseSetupQueueListeners can remove them.
 *
 * @param {Object} store - The Redux store.
 * @returns {boolean} True when the queue is set up.
 */
function setUpInterpreterQueue(store) {
    const role = getUserRole();
    const register = queueService.on;
    queueService.on = (event, callback) => {
        setupQueueListeners.push([event, callback]);
        register(event, callback);
    };
    try {
        const ready = initializeInterpreterQueue(store);
        setupRole = ready ? role : undefined;
        return ready;
    }
    finally {
        queueService.on = register;
    }
}
function releaseSetupQueueListeners() {
    const listeners = setupQueueListeners;
    setupQueueListeners = [];
    setupRole = undefined;
    listeners.forEach(([event, callback]) => queueService.off(event, callback));
}
/**
 * Sets the queue up again when the stored role is not the role of the current
 * setup. Every native sign-in calls it before the queue connects
 * (signedInLanding.ts), and the app calls it when it has read the stored
 * session (RootNavigationContainer). The middleware also calls it on each
 * native action.
 *
 * @returns {boolean} True when it set the queue up again.
 */
export function syncInterpreterQueueRole() {
    if (!_interpreterQueueMiddlewareInitialized || getUserRole() === setupRole) {
        return false;
    }
    const store = interpreterQueueStore;
    mobileLog('info', 'interpreter_queue_role_changed', { from: setupRole || null, to: getUserRole() }, { console: false });
    releaseSetupQueueListeners();
    resetOutgoingRing();
    resetScheduledRequest();
    heldSessionWatch?.stop();
    heldSessionWatch = undefined;
    _interpreterQueueMiddlewareInitialized = false;
    if (store) {
        _interpreterQueueMiddlewareInitialized = setUpInterpreterQueue(store);
    }
    return true;
}
// lib-jitsi-meet's JitsiConferenceErrors.CONFERENCE_MAX_USERS. The value, not
// an import: this middleware must not load the SDK (web-join-timeline guard).
const CONFERENCE_MAX_USERS_ERROR = 'conference.max_users';
const CONFERENCE_DESTROYED_ERROR = 'conference.destroyed';
// The call whose "media is up here" signal this page already sent.
let movedHereSignalledFor;
MiddlewareRegistry.register((store) => (next) => (action) => {
    // A manager's hidden observation is never the saved account's call
    // page: no queue, no room entry or departure, no stored-call changes.
    if (isObserverRoomPage()) {
        return next(action);
    }
    // Before the reducer: see noteInCallConferenceFailure.
    if (action.type === CONFERENCE_FAILED && hasStoredActiveCall()) {
        noteInCallConferenceFailure(action);
    }
    const wasOnline = action.type === SET_NETWORK_INFO
        ? store.getState()['features/base/net-info']?.isOnline : undefined;
    const result = next(action);
    notifyDesktopOfQueue(action, store.getState);
    if (action.type === MOVE_CALL_HERE && typeof action.callId === 'string' && typeof action.roomName === 'string') {
        openCallMovedHere(store, { callId: action.callId, roomName: action.roomName });
    }
    if (action.type === RESUME_HELD_CALL && typeof action.callId === 'string') {
        resumeHeldCallHere(store, action.callId);
    }
    if (action.type === SWITCH_TO_HELD_CALL && typeof action.callId === 'string') {
        switchToHeldCallHere(store, action.callId, action.mode === 'end' ? 'end' : 'hold');
    }
    if (action.type === END_HELD_CALL && typeof action.callId === 'string') {
        endHeldCallHere(store, action.callId);
    }
    if (action.type === MERGE_HELD_CALL && typeof action.callId === 'string') {
        mergeHeldCallHere(store, action.callId);
    }
    if (action.type === START_CALL_AFTER_BUSY) {
        void startCallAfterBusy(store, action);
    }
    if ((action.type === CONFERENCE_FAILED || action.type === CONNECTION_FAILED) && hasStoredActiveCall()) {
        recordConferenceJoinFailure({ error: action.error }, action.type);
    }
    if (action.type === CONNECTION_ESTABLISHED
        || action.type === DATA_CHANNEL_OPENED
        || action.type === TRACK_ADDED) {
        ensureJoinStartRestored();
    }
    if (action.type === CONNECTION_ESTABLISHED
        && hasStoredActiveCall()
        && queueRoomNavigationStartedAt
        && !queueRoomSignalingConnectedAt) {
        const activeCall = getStageEventCall();
        queueRoomSignalingConnectedAt = Date.now();
        emitMobileTelemetryEvent('vrs_signaling_connected', {
            elapsedMs: Math.max(0, queueRoomSignalingConnectedAt - queueRoomNavigationStartedAt),
            sinceBootMs: sinceDocumentStartMs(),
            role: getUserRole(),
            source: queueRoomJoinSource
        }, {
            callId: activeCall?.callId,
            requestId: activeCall?.requestId,
            roomName: activeCall?.roomName,
            state: 'connected'
        });
    }
    // This device's media connection to the bridge is open. If the
    // client's call was moving here, the server may now release the other
    // device: the interpreter's client pane moves here the moment it
    // leaves, so it must not happen before this device can send video.
    // (Joining the conference is too early: media can take seconds more.)
    // Also how a participant whose page went away says they are back in
    // a call held for them (vrs-server/ws/call-reconnect-grace.js), so the
    // interpreter sends it too.
    if (action.type === DATA_CHANNEL_OPENED && hasStoredActiveCall()
        && (getUserRole() === 'client' || getUserRole() === 'interpreter')) {
        const activeCall = getStoredActiveCall();
        if (activeCall?.callId && movedHereSignalledFor !== activeCall.callId) {
            movedHereSignalledFor = activeCall.callId;
            queueService.notifyCallRoomJoined(activeCall.callId);
        }
    }
    // Native: a room closed for everyone (a hang-up that ends the room,
    // or the server that closes it) arrives as conference.destroyed before
    // any leave. base/conference/middleware.native.ts then dispatches
    // conferenceLeft and hangup, never conferenceWillLeave, so nothing
    // sent call_end. The server kept the call live, and the next call got
    // "You are already in a call." (Robert, 2026-10-05). The room is gone,
    // so the call is over on this device too. A second call_end for a call
    // that already ended changes nothing on the server.
    if (action.type === CONFERENCE_FAILED && isNativeRuntime() && hasStoredActiveCall()
        && action.error?.name === CONFERENCE_DESTROYED_ERROR
        && action.conference && action.conference === joinedConferenceForActiveCall) {
        finalizeConferenceExit(store, 'conference_destroyed');
    }
    // A full room refuses this device. If the client's call was moving
    // here it stays on the other device, which the server tells why.
    if (action.type === CONFERENCE_FAILED && hasStoredActiveCall() && getUserRole() === 'client'
        && action.error?.name === CONFERENCE_MAX_USERS_ERROR) {
        const activeCall = getStoredActiveCall();
        if (activeCall?.callId) {
            queueService.notifyCallMoveFailed(activeCall.callId, 'room_full');
        }
    }
    if (action.type === DATA_CHANNEL_OPENED
        && hasStoredActiveCall()
        && queueRoomNavigationStartedAt
        && !queueRoomMediaConnectedAt) {
        const activeCall = getStageEventCall();
        queueRoomMediaConnectedAt = Date.now();
        emitMobileTelemetryEvent('vrs_media_connected', {
            elapsedMs: Math.max(0, queueRoomMediaConnectedAt - queueRoomNavigationStartedAt),
            signalingToMediaMs: queueRoomSignalingConnectedAt
                ? Math.max(0, queueRoomMediaConnectedAt - queueRoomSignalingConnectedAt)
                : undefined,
            sinceBootMs: sinceDocumentStartMs(),
            role: getUserRole(),
            source: queueRoomJoinSource
        }, {
            callId: activeCall?.callId,
            requestId: activeCall?.requestId,
            roomName: activeCall?.roomName,
            state: 'media_connected'
        });
    }
    if (action.type === TRACK_ADDED || action.type === TRACK_UPDATED) {
        noteVideoChange(action);
    }
    // Someone else is in the room: the ring this device placed was
    // answered, so leaving is a hang-up again (outgoingRing.ts).
    if (action.type === PARTICIPANT_JOINED) {
        const participant = action.participant;
        if (participant && !participant.local && !participant.fakeParticipant) {
            noteOutgoingRingAnswered(getStoredActiveCall()?.callId);
        }
    }
    const addedTrack = action.type === TRACK_ADDED
        ? action.track
        : undefined;
    if (addedTrack
        && !addedTrack.local
        && addedTrack.mediaType === 'video'
        && hasStoredActiveCall()
        && queueRoomNavigationStartedAt
        && !queueRoomRemoteVideoAt) {
        const activeCall = getStageEventCall();
        queueRoomRemoteVideoAt = Date.now();
        emitMobileTelemetryEvent('vrs_remote_video_received', {
            elapsedMs: Math.max(0, queueRoomRemoteVideoAt - queueRoomNavigationStartedAt),
            mediaToVideoMs: queueRoomMediaConnectedAt
                ? Math.max(0, queueRoomRemoteVideoAt - queueRoomMediaConnectedAt)
                : undefined,
            sinceBootMs: sinceDocumentStartMs(),
            role: getUserRole(),
            source: queueRoomJoinSource,
            transport: addedTrack.jitsiTrack?.isP2P === true ? 'p2p' : 'jvb'
        }, {
            callId: activeCall?.callId,
            requestId: activeCall?.requestId,
            roomName: activeCall?.roomName,
            state: 'remote_video'
        });
        // The timeline is complete; a stale marker must not be adopted by
        // the next room this tab opens.
        try {
            removePersistentItem(JOIN_TIMELINE_KEY);
        }
        catch { /* storage blocked */ }
    }
    if (action.type === CONFERENCE_JOINED && hasStoredActiveCall()) {
        const activeCall = getStoredActiveCall();
        if (isConferenceForActiveRoom(activeCall?.roomName, action.conference)) {
            conferenceJoinedForActiveCall = true;
            // Web: Home in another tab must know this call page is live,
            // and must not take the session back from it (roomTabPresence.ts).
            if (!isNativeRuntime() && getUserRole() === 'client' && !isObserverRoomPage()
                && !isRoomActivationHeld() && activeCall?.roomName) {
                startRoomTabPresence({
                    callId: activeCall.callId,
                    roomName: activeCall.roomName,
                    userId: getLocalUserId()
                });
            }
            joinedConferenceForActiveCall = action.conference;
            conferenceJoinFailure = undefined;
            preJoinExitHandledKey = undefined;
            const joinedAt = Date.now();
            activeConferenceJoinedAt = joinedAt;
            completeMediaRejoin(activeCall);
            callAwaySentFor = undefined;
            installPageLeaveListener();
            announceRoomEntered('conference_joined');
            if (action.conference) {
                observeMediaTransport(action.conference, {
                    callId: activeCall?.callId,
                    requestId: activeCall?.requestId,
                    roomName: activeCall?.roomName
                });
            }
            emitMobileTelemetryEvent('vrs_conference_joined', {
                elapsedMs: queueRoomNavigationStartedAt
                    ? Math.max(0, joinedAt - queueRoomNavigationStartedAt)
                    : undefined,
                signalingToConferenceMs: queueRoomSignalingConnectedAt
                    ? Math.max(0, joinedAt - queueRoomSignalingConnectedAt)
                    : undefined
            }, {
                callId: activeCall?.callId,
                requestId: activeCall?.requestId,
                roomName: activeCall?.roomName,
                state: 'joined'
            });
        }
    }
    // The VRI client pressed End call (leaveConference): the server hears
    // it before the others' apps, which hang up on the end-meeting message
    // first. Otherwise the session ended as the interpreter's, and the
    // client was told "The interpreter ended the session."
    if (action.type === CALL_END_BEFORE_NAVIGATION && hasStoredActiveCall()) {
        finalizeConferenceExit(store, String(action.reason || 'call_end_before_navigation'), { navigate: false });
    }
    if (action.type === VRI_SESSION_END_REQUESTED) {
        const activeCall = getStoredActiveCall();
        if (activeCall?.callId && typeof action.room === 'string'
            && activeCall.roomName?.toLowerCase() === action.room.toLowerCase()) {
            vriSessionEndSentFor = activeCall.callId;
            queueService.endCall(activeCall.callId, activeCall.roomName, 'hangup');
        }
    }
    if (action.type === CONFERENCE_WILL_LEAVE && hasStoredActiveCall()) {
        const activeCall = getStoredActiveCall();
        const mediaFailure = inCallMediaFailure && inCallMediaFailure.conference === action.conference
            ? inCallMediaFailure : undefined;
        // On web, conference.js leaves a room whose media session failed
        // by itself. That is not this person hanging up: the reload
        // overlay rejoins the call (inCallMediaRejoin.ts). Past the rejoin budget
        // the call ends as before.
        if (action.pageUnload === true && keepCallForPageLeave('beforeunload')) {
            // The page is going away, not hanging up (keepCallForPageLeave).
        }
        else if (mediaFailure?.rejoin) {
            keepCallForMediaRejoin('conference_will_leave');
        }
        else if (mediaFailure) {
            finalizeConferenceExit(store, 'media_rejoin_limit_reached');
        }
        else if (shouldPreserveMatchedCallAfterExit(conferenceJoinedForActiveCall, activeCall?.roomName, joinedConferenceForActiveCall, action.conference)) {
            preserveMatchedCallBeforeJoin('conference_will_leave');
        }
        else if (action.pageUnload === true) {
            // The page going away, not a hang-up: the server decides.
            finalizeConferenceExit(store, 'page_unload');
        }
        else {
            finalizeConferenceExit(store, 'conference_will_leave');
        }
    }
    const transportObserver = mediaTransportObserver;
    if (action.type === CONFERENCE_LEFT && transportObserver && transportObserver.conference === action.conference) {
        transportObserver.dispose();
    }
    if (action.type === CONFERENCE_LEFT && !isNativeRuntime()) {
        stopRoomTabPresence();
    }
    if (action.type === CONFERENCE_LEFT && hasStoredActiveCall()) {
        const activeCall = getStoredActiveCall();
        if (inCallMediaFailure?.rejoin && inCallMediaFailure.conference === action.conference) {
            // Native: the SDK left the failed room; PageReloadDialog rejoins.
            keepCallForMediaRejoin('conference_left');
        }
        else if (shouldPreserveMatchedCallAfterExit(conferenceJoinedForActiveCall, activeCall?.roomName, joinedConferenceForActiveCall, action.conference, false)) {
            preserveMatchedCallBeforeJoin('conference_left');
        }
        else {
            finalizeConferenceExit(store, 'conference_left');
        }
    }
    // Native: leaving from the reload dialog (Cancel, or hanging up)
    // instead of rejoining. The call was kept only for the rejoin, so
    // leaving ends it; otherwise nobody would, and the other person would
    // wait in the room.
    if (action.type === SET_ROOM && !action.room && inCallMediaFailure && hasStoredActiveCall()) {
        finalizeConferenceExit(store, 'media_rejoin_cancelled');
    }
    if (action.type === APP_STATE_CHANGED && action.appState === 'active') {
        startActiveCallReconciliationLoop(store);
        reconcileLocalActiveCall(store, 'app_foreground_reconcile');
        if (getUserRole() === 'interpreter'
            && queueService.getLastInterpreterStatus() === 'active'
            && !getStoredActiveCall()?.roomName) {
            queueService.reconnect();
        }
        else {
            // Every signed-in role: a socket that stopped its retries while
            // the app was away connects again (Robert, 2026-10-29).
            queueService.resumeConnection('foreground');
        }
    }
    // The phone is online again (base/net-info): the same for a socket that is down.
    if (action.type === SET_NETWORK_INFO && action.isOnline === true && wasOnline === false && isNativeRuntime()) {
        queueService.resumeConnection('network');
    }
    // Set the queue listeners up on the first action, and again in the
    // apps when the stored role changes (syncInterpreterQueueRole).
    if (!_interpreterQueueMiddlewareInitialized) {
        _interpreterQueueMiddlewareInitialized = setUpInterpreterQueue(store);
    }
    else if (isNativeRuntime()) {
        syncInterpreterQueueRole();
    }
    return result;
});
/**
 * Sign-out calls queueService.disconnect(), which clears every service
 * listener. Without this reset the middleware never re-subscribes, so offers
 * and matches after a re-login reach zero listeners.
 */
export function resetInterpreterQueueMiddleware() {
    _interpreterQueueMiddlewareInitialized = false;
    releaseSetupQueueListeners();
    forgetTransferAction();
    resetOutgoingRing();
    resetScheduledRequest();
    // Signed out: not Available (keep-awake off), no post-call card, and no
    // waiting count left for the next account's breather.
    interpreterQueueStore?.dispatch(interpreterAvailabilityChanged(false));
    interpreterQueueStore?.dispatch(interpreterSessionSuperseded(false));
    interpreterQueueStore?.dispatch(interpreterBreakChanged(false));
    interpreterQueueStore?.dispatch(dismissInterpreterWrapUp());
    forgetWrapUpQueueCount();
    // No outside-meeting card or desk state for the next account.
    stopMeetingSnapshotWait();
    interpreterQueueStore?.dispatch({ type: INTERPRETER_MEETING_CALL_ENDED });
    interpreterQueueStore?.dispatch({ type: MEETING_DESK_STATUS_CHANGED, meetingDesk: undefined });
    // No "In a call on another device" left up, or re-checked every 10 s,
    // for the old account.
    if (interpreterQueueStore) {
        setCallElsewhere(interpreterQueueStore);
    }
    else if (callElsewhereTimer) {
        clearInterval(callElsewhereTimer);
        callElsewhereTimer = undefined;
    }
    interpreterQueueStore = undefined;
}
/**
 * Initializes the interpreter queue by setting up event listeners
 * on the queue service and connecting them to Redux actions.
 *
 * @param {Object} store - The Redux store.
 */
function initializeInterpreterQueue(store) {
    const userRole = getUserRole();
    console.log('[InterpreterQueue] Initializing for role:', userRole);
    if (userRole === 'interpreter' && !hasInterpreterSessionToken()) {
        console.log('[InterpreterQueue] Waiting for interpreter authentication before connecting');
        return false;
    }
    startActiveCallReconciliationLoop(store);
    interpreterQueueStore = store;
    installAccountDeviceNotices(store);
    installPageLeaveListener();
    // Connection status events - all users
    queueService.on('connection', (data) => {
        store.dispatch(queueConnectionChanged(data.connected));
        // The socket's waiting count went with it (a drop, or superseded).
        if (!data.connected && userRole === 'interpreter') {
            forgetWrapUpQueueCount(store);
        }
    });
    queueService.on('notificationEvent', (data) => {
        if (!data.body || typeof data.type !== 'string' || !['admin_announcement', 'manager_message', 'manager_observer_presence'].includes(data.type)) {
            return;
        }
        const sentAt = typeof data.data?.sentAt === 'string' ? data.data.sentAt : String(Date.now());
        mobileLog('info', `queue_${data.type}_received`, {}, { console: false });
        store.dispatch(showNotification({
            titleKey: undefined,
            title: data.title || (data.type === 'manager_message' ? 'Manager note' : 'Queue announcement'),
            descriptionKey: undefined,
            description: data.body,
            uid: `${data.type}-${sentAt}`
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    });
    queueService.on('conferenceInvitationError', data => {
        // Invitation-specific answer failures are shown by the incoming-call surfaces.
        if (data.invitationId)
            return;
        store.dispatch(showNotification({
            titleKey: 'incomingCall.invitationDeliveryFailed',
            descriptionKey: data.message ? undefined : 'incomingCall.invitationDeliveryRetry',
            description: data.message,
            uid: `conference-invitation-error-${data.callId || 'refresh'}`
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    });
    // Client-specific events
    if (userRole === 'client') {
        // Client receives notification when their request is queued
        queueService.on('requestQueued', (data) => {
            console.log('[InterpreterQueue] Request queued, position:', data.position);
            store.dispatch(queueStatusUpdate({
                requestId: data.requestId,
                queuePosition: data.position
            }));
        });
        // An interpreter for the client's own meeting: there is no room to
        // enter, on this device or any other of the account's devices.
        const externalMeetingMatched = (data) => {
            const meeting = toExternalMeeting(data.externalMeeting);
            if (!meeting || !data.callId)
                return false;
            const current = store.getState()['features/interpreter-queue']?.externalMeetingCall;
            if (current?.callId === data.callId)
                return true;
            store.dispatch({
                type: EXTERNAL_MEETING_CALL_UPDATED,
                externalMeetingCall: {
                    callId: data.callId,
                    interpreterName: data.interpreterName || 'Your interpreter',
                    platform: meeting.platform,
                    roomName: data.roomName,
                    stage: 'joining'
                }
            });
            mobileLog('info', 'queue_external_meeting_matched', { platform: meeting.platform }, { console: false });
            return true;
        };
        // Calendar meetings that are starting (phase 2): the VRS Home card.
        queueService.on('meetingReminder', (data) => {
            const reminders = (Array.isArray(data.reminders) ? data.reminders : []).flatMap(item => {
                const parsed = meetingReminderSchema.safeParse(item);
                return parsed.success ? [parsed.data] : [];
            });
            if (reminders.length)
                store.dispatch({ type: MEETING_REMINDERS_ADDED, reminders });
        });
        queueService.on('externalMeetingProgress', (data) => {
            const current = store.getState()['features/interpreter-queue']?.externalMeetingCall;
            if (!current || current.callId !== data.callId || !data.event)
                return;
            store.dispatch({ type: EXTERNAL_MEETING_CALL_UPDATED, externalMeetingCall: { ...current, stage: data.event } });
        });
        // Client receives notification when a match is found
        queueService.on('matchFound', (data) => {
            console.log('[InterpreterQueue] Match found:', data);
            if (externalMeetingMatched(data))
                return;
            store.dispatch(matchFound(toMatchData(data)));
            autoEnterQueueRoom(store, data, {
                startWithAudioMuted: false,
                startWithVideoMuted: shouldStartClientVideoMuted(store, data)
            });
        });
        // Client receives notification when meeting is initiated
        queueService.on('meetingInitiated', (data) => {
            console.log('[InterpreterQueue] Meeting initiated:', data);
            if (externalMeetingMatched(data))
                return;
            // The interpreter's call-back went through: its offer is spent,
            // and they wait in the new room (docs/interpreted-group-calls.md).
            if (data.callback) {
                store.dispatch(dismissInterpreterWrapUp());
            }
            store.dispatch(meetingInitiated(toMatchData(data)));
            autoEnterQueueRoom(store, data, {
                startWithAudioMuted: false,
                startWithVideoMuted: shouldStartClientVideoMuted(store, data)
            });
        });
        // P2P call: ringing → auto-enter the room
        queueService.on('p2pRinging', (data) => {
            console.log('[InterpreterQueue] P2P ringing:', data);
            handleGroupRinging(store, data);
            enterPlatformCallRoom(store, data, data.calleeName, 'caller_ringing');
        });
        // An instant meeting this device asked for (instantMeeting.ts): a live
        // call with only this client. The server answers only the asking
        // socket, so only this device goes in.
        queueService.on('instantMeetingStarted', (data) => {
            enterPlatformCallRoom(store, { callId: data.callId, clientId: data.clientId, roomName: data.roomName }, undefined, 'instant_meeting');
        });
        // The caller's own ring, in the room (plan #71; the web since
        // 2026-10-02, webOutgoingRing.ts): Cancel, the 60 s expiry and a
        // hang-up before anyone answers send p2p_cancel. The server's
        // call_ended (or p2p_call_cancelled) closes the room.
        installOutgoingRing({
            activeCallId: () => getStoredActiveCall()?.callId,
            localUserId: getLocalUserId,
            onCallOver: callId => handleLinkedInterpretedCallEnded(store, {
                callId,
                endedBy: getLocalUserId(),
                endedByRole: 'client',
                surface: 'participant_call_cancel'
            }),
            onExpired: callId => {
                if (getStoredActiveCall()?.callId !== callId) {
                    cancelOutgoingRingOnExit(callId);
                    return;
                }
                // The web room gives way to the portal, which says it there.
                if (!isNativeRuntime()) {
                    rememberCallEndNotice(callId, NO_ANSWER_MESSAGE);
                }
                store.dispatch(showNotification({
                    titleKey: undefined,
                    title: NO_ANSWER_MESSAGE,
                    descriptionKey: undefined,
                    uid: 'p2p-ring-expired'
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                finalizeConferenceExit(store, 'outgoing_ring_expired');
            }
        });
        if (isNativeRuntime()) {
            // An interpreter request scheduled for later from the call (plan #75).
            installScheduledRequest();
        }
        else {
            resumeWebOutgoingRing();
        }
        // A hearing party dialed this client's VRS number: drive the same
        // deaf-accessible incoming-call overlay with the caller's number.
        // Answering joins the room the interpreter request was queued into.
        queueService.on('inboundVrsCall', (payload) => {
            if (!isRecord(payload) || !stringValue(payload.roomName)) {
                mobileLog('warn', 'inbound_vrs_call_invalid', {}, { console: false });
                return;
            }
            const data = {
                callerName: stringValue(payload.callerName),
                clientStartWithVideoMuted: typeof payload.clientStartWithVideoMuted === 'boolean'
                    ? payload.clientStartWithVideoMuted : undefined,
                fromNumber: stringValue(payload.fromNumber),
                requestId: stringValue(payload.requestId),
                roomName: stringValue(payload.roomName)
            };
            mobileLog('info', 'inbound_vrs_call_received', {}, { console: false });
            // Native devices are rung by the CallKit/FCM push for this same
            // event; the overlay is the web surface.
            const isNativeApp = typeof navigator !== 'undefined'
                && navigator.product === 'ReactNative';
            if (!isNativeApp && data.roomName) {
                store.dispatch(incomingP2PCall({
                    callId: data.requestId || data.roomName,
                    roomName: data.roomName,
                    callerId: data.fromNumber || '',
                    callerName: data.callerName
                        || getPhoneCallLabel('vrs', data.fromNumber)
                        || 'Incoming VRS call',
                    clientStartWithVideoMuted: data.clientStartWithVideoMuted,
                    hasVideo: true,
                    groupCall: false,
                    inboundVrs: true
                }));
            }
        });
        queueService.on('p2pIncomingCall', (data) => {
            console.log('[InterpreterQueue] P2P incoming call:', data);
            rememberPlatformCallPeer(data.callId, data.callerId, data.callerName);
            rememberCallPhoto(data.callId, data.callerPhotoUrl);
            // Native has its own CallKit + IncomingPlatformCallSheet flow
            // (subscribed directly to this event); on web nothing surfaced the
            // call at all. Drive the deaf-accessible incoming-call overlay.
            const isNative = typeof navigator !== 'undefined'
                && navigator.product === 'ReactNative';
            if (!isNative && data.callId && data.roomName) {
                store.dispatch(incomingP2PCall({
                    callId: data.callId,
                    roomName: data.roomName,
                    callerId: data.callerId || '',
                    callerName: data.callerName || `${getAppName()} contact`,
                    callerPhotoUrl: profilePhotoPath(data.callerPhotoUrl),
                    clientStartWithVideoMuted: data.clientStartWithVideoMuted,
                    hasVideo: data.hasVideo !== false,
                    conferenceInvitation: data.conferenceInvitation,
                    invitationId: data.invitationId,
                    expiresAt: data.expiresAt,
                    groupCall: Boolean(data.groupCall)
                }));
            }
        });
        queueService.on('p2pJoinRoom', (data) => {
            console.log('[InterpreterQueue] P2P join room:', data);
            // An answer this device gave up on (its system call already ended
            // here) won late: hang the call up rather than open a room the
            // person saw end (abandonedAnswers.ts).
            if (data.callId && takeAbandonedAnswer(data.callId)) {
                mobileLog('info', 'platform_answer_late_join_hung_up', { callId: data.callId }, { console: false });
                queueService.endCall(data.callId, data.roomName);
                return;
            }
            // The answering side: the name the ring carried wins. Native sends
            // p2p_accept without a callerName, and a placeholder the answer
            // echoes back must not overwrite the ring's real name.
            const callerName = platformCallPeerName(data.callId, data.callerId, data.callerName);
            const waitingAnswer = takeCallWaitingAnswer(data.callId);
            if (waitingAnswer) {
                leaveCallForWaitingCall(store, waitingAnswer, data.callId);
            }
            rememberPlatformCallPeer(data.callId, data.callerId, callerName);
            enterPlatformCallRoom(store, data, callerName, 'answer');
        });
        queueService.on('interpreterCallbackStatus', (data) => showInterpreterCallbackStatus(store, data));
        // An interpreter's call-back: someone who answered waits on the
        // holding screen (AnchorHoldSheet) until the person the interpreter
        // was with is back, then comes into the room from here. The web
        // portal does the same itself (client-profile.js), so this is the
        // apps' path only.
        queueService.on('anchorHold', (data) => {
            if (data.callId) {
                heldCallbacks.set(data.callId, { anchorId: data.anchorId, anchorName: data.anchorName,
                    interpreterName: data.interpreterName });
            }
        });
        queueService.on('anchorHoldEnded', (data) => {
            if (data.callId) {
                heldCallbacks.delete(data.callId);
            }
        });
        // Merge (Robert, 2026-10-12): a held call joined another.
        queueService.on('callMerged', (data) => enterMergedCall(store, data));
        queueService.on('callMergeDone', (data) => {
            if (store.getState()['features/call-management']?.heldCall?.callId === data.heldCallId) {
                store.dispatch(heldCallCleared(data.heldCallId));
            }
            // The call is a group now, and the merger hosts it (Robert, 2026-10-29).
            markPlatformCallGroup(data.callId);
            recordPlatformCallHost(data.callId, data.hostId || undefined);
            store.dispatch(showNotification({
                title: 'Calls merged',
                uid: `call-merged-${data.callId}`
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        });
        queueService.on('anchorHoldReleased', (data) => {
            const held = data.callId ? heldCallbacks.get(data.callId) : undefined;
            if (!data.callId || !data.roomName || !held || !isNativeRuntime()) {
                return;
            }
            heldCallbacks.delete(data.callId);
            const anchorName = held.anchorName || undefined;
            rememberPlatformCallPeer(data.callId, held.anchorId, anchorName);
            enterPlatformCallRoom(store, {
                callId: data.callId,
                clientId: held.anchorId,
                groupCall: true,
                interpreterName: held.interpreterName || undefined,
                roomName: data.roomName
            }, anchorName, 'call_back');
        });
        queueService.on('p2pAccepted', (data) => {
            console.log('[InterpreterQueue] P2P accepted:', data);
            rememberPlatformCallPeer(data.callId, data.calleeId, data.calleeName);
            if (data.groupCall === true) {
                markPlatformCallGroup(data.callId);
            }
            recordPlatformCallHost(data.callId, data.hostId);
        });
        // Group calls: who came and went, briefly, for everyone in the room.
        queueService.on('p2pParticipantJoined', data => showPlatformParticipantNotice(store, 'joined', data));
        queueService.on('p2pParticipantLeft', data => showPlatformParticipantNotice(store, 'left', data));
        // Someone joining makes it a group: its host may end it for everyone.
        queueService.on('p2pParticipantJoined', data => markPlatformCallGroup(data.callId));
        // The host passes on when the host leaves; End call for everyone goes with it.
        queueService.on('p2pParticipantJoined', data => recordPlatformCallHost(data.callId, data.hostId));
        queueService.on('p2pParticipantLeft', data => recordPlatformCallHost(data.callId, data.hostId));
        // An interpreter took the caller's in-call request: record them on the
        // stored call so the room's interpreter button (web and native) stops
        // offering another, including after a reload.
        queueService.on('p2pInterpreterJoined', (data) => {
            recordP2PInterpreterOnCall(data?.callId, data?.interpreterName, { anchorId: data?.anchorId, anchorName: data?.anchorName });
        });
        // The anchor left a group call that goes on: the interpreter stays,
        // tied to and billed to the next Deaf person (Robert, 2026-10-05).
        queueService.on('p2pAnchorChanged', data => handleP2PAnchorChanged(store, data));
        // The interpreter left a call that started person-to-person: it goes
        // on without them, and whoever placed it may ask for another.
        queueService.on('p2pInterpreterLeft', data => {
            recordP2PInterpreterLeft(typeof data?.callId === 'string' ? data.callId : undefined);
            store.dispatch(showNotification({
                titleKey: undefined,
                title: interpreterLeftNotice(data),
                descriptionKey: undefined,
                uid: 'p2p-interpreter-left'
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        });
        // The caller auto-enters the room on ringing, so a decline arriving
        // after that must be surfaced IN the room — otherwise they wait in an
        // empty room with no feedback.
        queueService.on('p2pDeclined', (data) => {
            console.log('[InterpreterQueue] P2P declined:', data);
            showP2PDeclinedNotice(store, data);
            if (!isNativeRuntime()) {
                leaveDeclinedWebRing(store, data);
            }
        });
        queueService.on('p2pGroupNoAnswer', data => handleP2PGroupNoAnswer(store, data));
        // P2P call: target offline / DND / failed — clear any web incoming
        // overlay and log.
        queueService.on('p2pCallFailed', (data) => {
            console.warn('[InterpreterQueue] P2P call failed:', data.message);
            store.dispatch(clearIncomingP2PCall());
        });
        // No answer / unreachable: the caller auto-entered the room on
        // ringing, so tell them there and take them back out. Mobile and the
        // home page already present deliveryReason; the web room did not.
        queueService.on('p2pTargetOffline', data => {
            const copy = describeP2PUnavailable(data, getAppName());
            console.warn('[InterpreterQueue] P2P target offline:', data.calleeName, data.deliveryReason);
            store.dispatch(showNotification({
                titleKey: undefined,
                title: copy.title,
                descriptionKey: undefined,
                description: copy.detail || undefined,
                uid: 'p2p-target-offline',
                // The room closes: say it on Home too (AppNoticeHost).
                keepAfterCall: true
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            const parked = lastPlatformCall
                && (lastPlatformCall.callId || '') === (String(data.callId || ''))
                && getCurrentBrowserRoomName() === lastPlatformCall.roomName;
            if (parked) {
                lastPlatformCall = undefined;
                // The web room gives way to the portal: its "didn't answer"
                // card, with Leave a VideoMail (webOutgoingRing.ts).
                if (!isNativeRuntime()) {
                    rememberUnansweredCall(data);
                    finalizeConferenceExit(store, 'outgoing_ring_unanswered');
                    return;
                }
                store.dispatch(hangup(true));
            }
        });
        queueService.on('p2pTargetDnd', data => {
            console.warn('[InterpreterQueue] P2P target DND:', data.calleeName);
        });
    }
    // Interpreter-specific events
    if (userRole === 'interpreter') {
        // Outside meetings (docs/outside-meeting-desk.md): the interpreter's
        // web console desk. An outside-meeting offer can be accepted here
        // only while it is connected (InterpreterOfferSheet).
        queueService.on('meetingDeskStatus', (data) => {
            const meetingDesk = meetingDeskFromStatus(data);
            if (meetingDesk) {
                store.dispatch({ type: MEETING_DESK_STATUS_CHANGED, meetingDesk });
            }
        });
        // The outside-meeting call, after the accept or at a sign-in during
        // it. The server keeps the interpreter out of the queue until it
        // ends: a reconnect must not announce Available again.
        queueService.on('externalMeetingCall', (data) => {
            const current = store.getState()['features/interpreter-queue']?.interpreterMeetingCall;
            if (meetingSnapshotWait?.callId === data.callId) {
                stopMeetingSnapshotWait();
            }
            queueService.holdAvailabilityReplay();
            if (current?.callId !== data.callId) {
                store.dispatch(dismissInterpreterWrapUp());
            }
            store.dispatch({
                type: INTERPRETER_MEETING_CALL_UPDATED,
                interpreterMeetingCall: meetingCallFromSnapshot(data, current, Date.now())
            });
            if (current?.callId !== data.callId) {
                mobileLog('info', 'interpreter_meeting_call_received', {
                    entered: Boolean(data.enteredAt),
                    platform: data.platform || null
                }, { console: false });
            }
        });
        // A step that the server recorded, from this phone or from the desk.
        queueService.on('externalMeetingStatusAck', (data) => {
            const current = store.getState()['features/interpreter-queue']?.interpreterMeetingCall;
            if (!current || current.callId !== data.callId) {
                return;
            }
            store.dispatch({ type: INTERPRETER_MEETING_CALL_UPDATED, interpreterMeetingCall: withMeetingAck(current, data) });
        });
        // Signed in again during a meeting call: wait for the server to send it again.
        queueService.on('authenticated', () => {
            const current = store.getState()['features/interpreter-queue']?.interpreterMeetingCall;
            stopMeetingSnapshotWait();
            if (!current) {
                return;
            }
            const callId = current.callId;
            meetingSnapshotWait = {
                callId,
                timer: setTimeout(() => {
                    meetingSnapshotWait = undefined;
                    if (store.getState()['features/interpreter-queue']?.interpreterMeetingCall?.callId !== callId) {
                        return;
                    }
                    mobileLog('info', 'interpreter_meeting_call_gone_after_sign_in', {}, { console: false });
                    store.dispatch({ type: INTERPRETER_MEETING_CALL_ENDED, callId });
                }, MEETING_SNAPSHOT_WAIT_MS)
            };
        });
        // The server's confirmation, not the local wish: Malka Terp keeps the
        // screen awake only while it is really in the queue.
        queueService.on('statusUpdated', (data) => {
            const status = typeof data?.status === 'string' ? data.status.toLowerCase() : '';
            store.dispatch(interpreterAvailabilityChanged(status === 'active' || status === 'available'));
        });
        // The account's queue went to another device. Malka Terp used to go
        // quiet here ("Reconnecting…" forever): the state lets it say so and
        // offer "Use this device instead" (InterpreterSessionNotice).
        queueService.on('sessionSuperseded', (data) => {
            store.dispatch(interpreterSessionSuperseded(true, typeof data?.message === 'string' ? data.message.slice(0, 300) : undefined));
        });
        // Interpreter receives incoming interpreter requests
        queueService.on('interpreterRequest', (data) => {
            console.log('[InterpreterQueue] New interpreter request:', data);
            store.dispatch(interpreterRequestReceived(toInterpreterRequest(data)));
        });
        // A colleague's warm transfer: the same full-screen offer, answered
        // with call_transfer_accept / call_transfer_cancel (actions.ts).
        queueService.on('callTransferOffer', data => {
            const card = transferOfferToRequest(data);
            if (!card) {
                return;
            }
            mobileLog('info', 'call_transfer_offer_received', { hasCallId: Boolean(card.transfer?.callId) }, { console: false });
            store.dispatch(interpreterRequestReceived(card));
        });
        // A colleague took this interpreter's call: they stay until the server
        // releases them, and their own hang-up meanwhile only leaves
        // (base/conference/actions.ts leaveConference, callHandedOver).
        queueService.on('callTransferAccepted', (data) => {
            const callId = isRecord(data) ? stringValue(data.callId) : undefined;
            const stored = getStoredActiveCall();
            if (callId && stored?.callId === callId) {
                setPersistentItem('vrs_active_call', JSON.stringify({ ...stored, handedOver: true }));
                const name = isRecord(data) ? stringValue(data.acceptedByName) : undefined;
                store.dispatch(showNotification({
                    titleKey: undefined,
                    title: transferTakingOverText(name),
                    descriptionKey: undefined,
                    uid: `call-transfer-accepted-${callId}`
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            }
        });
        // A transfer's phone leg moved to this receiver only on a retry: kept
        // with the call for the room's next entry, and joined now if the room
        // is up (it joins a stored leg on entry).
        queueService.on('callPhoneLegHandedOver', (data) => {
            const stored = getStoredActiveCall();
            if (!stored?.callId || stored.callId !== data.callId) {
                return;
            }
            setPersistentItem('vrs_active_call', JSON.stringify({ ...stored, phoneLeg: data.phoneLeg }));
            const conference = store.getState()?.['features/base/conference'];
            const leg = storedTransferredPhoneLeg(stored.roomName, stored.callId);
            if (leg && conference?.conference && (!stored.roomName || conference.room === stored.roomName)) {
                void twilioVoiceService.joinTransferredPhoneLeg(leg).catch(() => undefined);
            }
        });
        // Both interpreters: the phone leg stayed with the sender, who stays.
        queueService.on('callTransferPhoneLegFailed', (data) => {
            if (getStoredActiveCall()?.callId !== data.callId) {
                return;
            }
            store.dispatch(showNotification({
                titleKey: undefined,
                title: TRANSFER_PHONE_LEG_FAILED_TEXT,
                descriptionKey: undefined,
                uid: `call-transfer-phone-leg-failed-${data.callId}`
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        });
        // The server's acknowledgement of this interpreter's decline (the
        // sender gets the same message type for their own transfer, which
        // matches no card here).
        queueService.on('callTransferCancelled', (data) => {
            const transferId = isRecord(data) ? stringValue(data.transferId) : undefined;
            if (transferId) {
                removeOfferCard(store, transferCardId(transferId));
            }
        });
        // The call ended, or its sender withdrew the transfer, while the offer
        // was up (vrs-server/ws/call-transfer-offers.js): the card goes and
        // Home says which.
        queueService.on('callTransferOfferEnded', data => {
            const cardId = transferCardId(data.transferId);
            const hadCard = store.getState()['features/interpreter-queue']?.pendingRequests
                ?.some((request) => request.id === cardId);
            if (!hadCard) {
                return;
            }
            removeOfferCard(store, cardId);
            store.dispatch({ type: QUEUE_SERVICE_ERROR, error: transferOfferEndedText(data.reason) });
            mobileLog('info', 'call_transfer_offer_ended', { reason: data.reason || null }, { console: false });
        });
        // A colleague's team request (teamOffers.ts): the same full-screen
        // offer, answered with team_accept / team_decline (actions.ts).
        queueService.on('teamOffer', data => {
            const card = teamOfferToRequest(data);
            if (card) {
                store.dispatch(interpreterRequestReceived(card));
            }
        });
        // Taken, withdrawn, lapsed, declined here or on another device, or
        // the call ended (vrs-server/ws/interpreter-teams.js).
        queueService.on('teamOfferEnded', data => {
            const cardId = teamCardId(data.teamId);
            const hadCard = store.getState()['features/interpreter-queue']?.pendingRequests
                ?.some((request) => request.id === cardId);
            if (!hadCard) {
                return;
            }
            removeOfferCard(store, cardId);
            const notice = teamOfferEndedText(data.reason);
            if (notice) {
                store.dispatch({ type: QUEUE_SERVICE_ERROR, error: notice });
            }
        });
        // This interpreter won the team offer: into the call's room as its
        // teammate. The room's tools read `team` from the match.
        queueService.on('teamJoined', data => {
            const match = teamJoinedMatch(data);
            removeOfferCard(store, teamCardId(data.teamId));
            if (!match.roomName) {
                return;
            }
            store.dispatch(meetingInitiated(match));
            autoEnterQueueRoom(store, {
                callId: match.callId,
                callType: match.callType,
                clientName: match.clientName,
                interpreterId: match.interpreterId,
                interpreterName: match.interpreterName,
                requestId: match.requestId,
                roomName: match.roomName,
                team: match.team
            }, { startWithAudioMuted: false, startWithVideoMuted: false });
        });
        // The team is over for its teammate (released, the call ended or
        // moved, or they left on another device): the room closes. The call
        // itself goes on for everyone else.
        queueService.on('teamEnded', data => {
            const activeCall = getStoredActiveCall();
            if (!activeCall?.team?.teamId || activeCall.team.teamId !== data.teamId) {
                return;
            }
            handleLinkedInterpretedCallEnded(store, {
                callId: activeCall.callId,
                endedByRole: 'interpreter',
                reason: `team_${data.reason || 'ended'}`,
                surface: 'team_ended'
            });
        });
        // Interpreter receives notification when request is assigned
        queueService.on('requestAssigned', (data) => {
            console.log('[InterpreterQueue] Request assigned:', data);
            // Meeting calls run from the desktop console (see requestAccepted).
            if (toExternalMeeting(data.externalMeeting))
                return;
            store.dispatch(meetingInitiated(toMatchData(data)));
            autoEnterQueueRoom(store, data, {
                startWithAudioMuted: false,
                startWithVideoMuted: false
            });
        });
        // A call the interpreter placed ("Call a client") and an admin's
        // transfer to this interpreter arrive as meeting_initiated alone,
        // with no request_accepted, and on native nothing else took the
        // interpreter into the room: Malka Terp said "Calling Dana…" and
        // stayed on Home. The web console enters on either message. Queue
        // and transfer accepts send both; the room navigation is keyed by
        // callId (autoEnterQueueRoom), so the second one changes nothing.
        queueService.on('meetingInitiated', (data) => {
            if (toExternalMeeting(data.externalMeeting) || !getQueueRoomName(data))
                return;
            clearTransferCardsForCall(store, data.callId);
            store.dispatch(meetingInitiated(toMatchData(data)));
            autoEnterQueueRoom(store, data, {
                startWithAudioMuted: false,
                startWithVideoMuted: false
            });
        });
    }
    // Common events for both roles
    // Summaries update counts and the client's own position. Interpreter
    // cards belong exclusively to targeted offers and their lifecycle events.
    queueService.on('queueStatus', (data) => {
        const currentRequestId = store.getState()['features/interpreter-queue']?.currentRequestId;
        const ownRequest = userRole === 'client' && currentRequestId
            ? data.pendingRequests.find(request => request.id === currentRequestId)
            : undefined;
        const position = ownRequest?.position;
        store.dispatch(queueStatusUpdate({
            activeInterpreters: data.activeInterpreters?.length || 0,
            queuePosition: typeof position === 'number' && Number.isInteger(position) && position > 0
                ? position : undefined,
            queueSize: typeof data.queueSize === 'number' ? data.queueSize : undefined
        }));
        // A waiting client this interpreter could take ends their post-call
        // breather early (the server's count; queueSize counts every request).
        if (userRole === 'interpreter') {
            noteWrapUpQueueStatus(store, data.eligibleQueueSize);
        }
    });
    // A request left the queue: the client cancelled, it timed out, or the
    // server withdrew it because another interpreter took it. Registered for
    // BOTH roles — this used to live in the client-only block, so on the terp
    // app `request_withdrawn` never cleared the incoming card and interpreters
    // were left an Accept button for a call that no longer existed.
    queueService.on('requestCancelled', (data) => {
        console.log('[InterpreterQueue] Request cancelled');
        // A scheduled request was never waiting here: its cancel must not
        // clear the live request or the call's match (scheduledRequest.ts).
        if (data.scheduled === true) {
            return;
        }
        const timedOut = data.timedOut === true || data.reason === 'timeout';
        if (getUserRole() === 'interpreter' && data.requestId) {
            const hadOffer = store.getState()['features/interpreter-queue']?.pendingRequests
                ?.some((request) => request.id === data.requestId);
            store.dispatch({ type: REQUEST_DECLINED, requestId: data.requestId });
            if (data.reason === 'offer_expired' && hadOffer) {
                store.dispatch(showNotification({
                    titleKey: undefined,
                    title: 'Interpreter offer expired',
                    descriptionKey: undefined,
                    uid: `queue-offer-expired-${data.requestId}`
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            // An admin paused the queue: the offer comes back when it resumes,
            // so this must not read as a decline or an expiry.
            if (data.reason === 'queue_paused' && hadOffer) {
                store.dispatch(showNotification({
                    titleKey: undefined,
                    title: 'Queue paused',
                    descriptionKey: undefined,
                    uid: `queue-offer-paused-${data.requestId}`
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            // An admin changed this interpreter's service regions and the
            // request is no longer theirs to take (docs/regional-queue-routing.md).
            if (data.reason === 'service_region_revoked' && hadOffer) {
                store.dispatch(showNotification({
                    titleKey: undefined,
                    title: 'Service regions changed',
                    descriptionKey: undefined,
                    description: 'This request is outside your service regions now.',
                    uid: `queue-offer-region-${data.requestId}`
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
        }
        else {
            // Say why, as web does, but only when a request was waiting here:
            // the Cancel button clears first, so its own reply stays quiet.
            // VRS Home and the VRI console turn requestNotice into a notice
            // (requestNotice.ts): the timeout is "No interpreter is
            // available." with "Try again", one text on every phone surface.
            const wasWaiting = Boolean(store.getState()['features/interpreter-queue']?.isRequestPending);
            const reason = timedOut
                ? 'No interpreter is available.'
                : data.reason === 'service_region_revoked'
                    ? 'Your account is no longer set up for calls in that region. Contact support.'
                    : 'Your interpreter request was cancelled.';
            store.dispatch({
                type: CANCEL_REQUEST,
                notice: wasWaiting ? reason : undefined
            });
            removePersistentItem('vrs_own_queue_request');
        }
        if (timedOut) {
            emitMobileTelemetryEvent('queue_request_timed_out', {}, {
                requestId: data.requestId,
                state: 'timed_out'
            });
        }
    });
    // An admin kicked this account. InterpreterQueueService already stops
    // reconnecting and clears the stored credentials, but on native that alone
    // leaves the app sitting on VRS/Interpreter Home looking signed in until it
    // is force-quit — which is exactly how the kick read on device 2026-08-02.
    // The web surfaces do their own reload in client-profile.js and
    // interpreter-profile.html, so only native needs routing here.
    // Two ways the same thing arrives. The live one is admin_force_logout, sent
    // while the socket is open. The delayed one is a SESSION_REVOKED auth_error
    // on the next reconnect, which is what a kicked device sees when it was
    // backgrounded, offline, or on an older build when the kick landed — that
    // was the case in the 2026-08-03 device report, where the app stayed on Home
    // and merely printed "Session has been revoked" under Request Interpreter.
    const handleForcedSignOut = (event, defaultTitle) => (data) => {
        console.warn(`[InterpreterQueue] Forced sign-out (${event}):`, data);
        mobileLog('warn', 'interpreter_queue_forced_signout', {
            event,
            reason: typeof data?.reason === 'string' ? data.reason : undefined
        }, { console: false });
        // The web surfaces reload themselves in client-profile.js and
        // interpreter-profile.html; running this there would double-handle it.
        if (!isNativeRuntime()) {
            return;
        }
        // Leave any live conference first; resetRoot alone can leave the
        // Jitsi room mounted underneath on Android.
        try {
            store.dispatch(hangup(true));
        }
        catch {
            mobileLog('warn', 'interpreter_queue_forced_signout_hangup_failed', { event }, { console: false });
        }
        store.dispatch(showNotification({
            titleKey: undefined,
            title: defaultTitle,
            descriptionKey: undefined,
            description: typeof data?.reason === 'string' && data.reason
                ? data.reason
                : 'Sign in again.',
            uid: FORCED_SIGN_OUT_NOTIFICATION_ID,
            // Still said on the sign-in screen after the call closes (AppNoticeHost).
            keepAfterCall: true
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        signOutMobileUser();
    };
    queueService.on('adminForceLogout', handleForcedSignOut('adminForceLogout', 'Signed out by an administrator'));
    queueService.on('sessionRevoked', handleForcedSignOut('sessionRevoked', 'Your session has ended'));
    // The one sign-in (docs/unified-sign-in.md, "The phone apps"): "Sign out
    // of all apps" or a sign-out in another app of the sign-in arrives live as
    // signed_out. A new password on any device, or an ended sign-in while the
    // socket was away, arrives as a refused refresh of any request.
    queueService.on('signedOut', handleForcedSignOut('signedOut', 'Signed out'));
    stopAuthSessionEndedListener?.();
    stopAuthSessionEndedListener = onAuthSessionEnded(() => handleForcedSignOut('authSessionEnded', 'Your session has ended')({}));
    queueService.on('requestAccepted', (data) => {
        console.log('[InterpreterQueue] Request accepted:', data);
        if (getUserRole() === 'interpreter') {
            if (data.requestId) {
                store.dispatch({ type: REQUEST_ACCEPTED, requestId: data.requestId });
            }
            // A transfer accept carries no requestId: its card goes by callId.
            clearTransferCardsForCall(store, data.callId);
            // An outside meeting opens on the web console, never in a room here.
            if (toExternalMeeting(data.externalMeeting)) {
                startInterpreterMeetingCall(store, data);
                return;
            }
            store.dispatch(meetingInitiated(toMatchData(data)));
            autoEnterQueueRoom(store, data, {
                startWithAudioMuted: false,
                startWithVideoMuted: false
            });
        }
    });
    queueService.on('requestDeclined', (data) => {
        console.log('[InterpreterQueue] Request declined:', data);
        if (getUserRole() === 'interpreter' && data.requestId) {
            store.dispatch({ type: REQUEST_DECLINED, requestId: data.requestId });
        }
    });
    queueService.on('authenticated', (data) => {
        store.dispatch({ type: QUEUE_CAPABILITIES_UPDATED, capabilities: data.capabilities || {} });
        reconnectGraceMs = data.capabilities?.reconnectGraceMs;
        // Signed in (a page load, or the socket back after a drop): if this
        // device is in its call's room, it is in the call (the reconnect grace).
        announceRoomEntered('authenticated');
    });
    // The reconnect grace (vrs-server/ws/call-reconnect-grace.js): someone in
    // this call left without hanging up and it is held for them. The partner
    // pane says so in a one-to-one call (vrs-layout, PeerWaitingPane); a group
    // call, where the absent person is the one the interpreter is tied to,
    // gets a banner.
    queueService.on('peerReconnecting', data => {
        if (!callNoticeBelongsHere(data.callId) || !data.userId
            || (data.role !== 'client' && data.role !== 'interpreter')) {
            return;
        }
        const now = Date.now();
        const seconds = typeof data.secondsLeft === 'number' && data.secondsLeft >= 0
            ? Math.round(data.secondsLeft) : undefined;
        const awaySince = data.awaySince ? Date.parse(data.awaySince) : NaN;
        const peer = {
            anchor: data.anchor === true,
            awaySince: Number.isFinite(awaySince) ? Math.min(awaySince, now) : now,
            callId: data.callId,
            cause: data.cause || 'disconnected',
            graceEndsAt: now + ((seconds ?? 20) * 1000),
            name: data.name || null,
            role: data.role,
            userId: data.userId
        };
        store.dispatch({ type: VRS_PEER_RECONNECTING, peer });
        const activeCall = getStoredActiveCall();
        if (activeCall && isGroupCallRecord(activeCall)) {
            // The words are shared with the portal and native
            // (shared/interpreted-anchor.js).
            store.dispatch(showNotification({
                titleKey: undefined,
                title: anchorAwayBanner(peer, now),
                descriptionKey: undefined,
                uid: CALL_PARTICIPANT_AWAY_UID
            }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
        }
        mobileLog('info', 'vrs_call_peer_reconnecting', {
            anchor: data.anchor === true,
            callId: data.callId,
            cause: data.cause,
            role: data.role
        }, { console: false });
    });
    queueService.on('peerReconnected', data => {
        if (!callNoticeBelongsHere(data.callId)) {
            return;
        }
        const name = data.userId
            ? store.getState()?.['features/vrs-layout']?.peersReconnecting?.[data.userId]?.name
            : undefined;
        store.dispatch({ type: VRS_PEER_RECONNECTED, callId: data.callId, userId: data.userId });
        const activeCall = getStoredActiveCall();
        if (activeCall && isGroupCallRecord(activeCall)) {
            store.dispatch(hideNotification(CALL_PARTICIPANT_AWAY_UID));
            store.dispatch(showNotification({
                titleKey: undefined,
                title: `${participantLabel(data.role, name)} is back`,
                descriptionKey: undefined,
                uid: 'call-participant-back'
            }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
        }
        mobileLog('info', 'vrs_call_peer_reconnected', {
            awayMs: data.awayMs,
            callId: data.callId,
            role: data.role
        }, { console: false });
    });
    // Native: this person's own call is held for them; go back into it.
    queueService.on('callReconnectAvailable', data => {
        reenterHeldCall(store, data);
    });
    queueService.on('callEnded', (data) => {
        console.log('[InterpreterQueue] Interpreted call ended:', data);
        store.dispatch(hideNotification(CALL_PARTICIPANT_AWAY_UID));
        if (data.callId)
            store.dispatch({ type: VRS_PEER_RECONNECTED, callId: data.callId });
        if (data.callId)
            store.dispatch({ type: EXTERNAL_MEETING_CALL_ENDED, callId: data.callId });
        // The interpreter's outside-meeting call has no room to leave.
        if (endInterpreterMeetingCall(store, data))
            return;
        handleLinkedInterpretedCallEnded(store, data);
    });
    // Error handling
    queueService.on('error', (data) => {
        // A refused p2p_cancel belongs to the room's Cancel (outgoingRing.ts),
        // and a refused schedule to its sheet (scheduledRequest.ts).
        if (isOutgoingRingCancelError(data) || isScheduledRequestError(data)) {
            return;
        }
        // A refused step of an outside meeting belongs to its card on Home.
        if (meetingStepRefused(store, data)) {
            return;
        }
        // A refused call-back belongs to the wrap-up card that asked.
        if (data.operation === 'interpreter_callback') {
            store.dispatch(callBackRefused(data));
            return;
        }
        // An in-call invite's refusal (call full, not found…) belongs to its
        // row in the invite panel, not to the interpreter queue.
        // Nor does a refused group-call rejoin (the room page reports it).
        if (data.operation === 'conference_add' || isGroupRejoinPending()) {
            return;
        }
        // Nor does the host's end-for-all refusal (the hang-up menu reports it).
        if (data.operation === 'p2p_end_for_all') {
            return;
        }
        // Nor does a refused instant meeting (the screen that asked reports it).
        if (data.operation === 'instant_meeting_start') {
            return;
        }
        // Nor does a refused guest link: the invite panel or VRI console that
        // asked reports it, in its own words.
        if (data.operation === 'prepare_vri_invite') {
            return;
        }
        // Nor a refused transfer or withdrawal: the call tools that sent it
        // show it (interpreter-tools/useCallTransfer.ts).
        if (data.operation === 'call_transfer' || data.operation === 'call_transfer_cancel') {
            return;
        }
        if (data.code === 'QUEUE_WS_CONNECTION_FAILED') {
            store.dispatch(queueConnectionChanged(false, data.message || 'Unable to connect to queue server.'));
            return;
        }
        // A team request's refusals belong to the room's tools (teamOffers.ts
        // reduceCallTeam); a refused team accept takes its card down and the
        // message below stays on Home.
        if (data.operation === 'team_request' || data.operation === 'team_cancel' || data.operation === 'team_leave') {
            return;
        }
        const refusedTeamId = data.teamId;
        if (data.operation === 'team_accept' && typeof refusedTeamId === 'string') {
            removeOfferCard(store, teamCardId(refusedTeamId));
        }
        // A transfer the interpreter just answered is gone (the call ended or
        // moved, or it was taken): its card goes, and the message below stays
        // on Home.
        const endedTransferCard = cardEndedByError(lastTransferAction(), data);
        if (endedTransferCard) {
            removeOfferCard(store, endedTransferCard);
        }
        mobileLog('error', 'interpreter_queue_service_error', {
            code: data.code,
            message: data.message,
            retrying: data.retrying
        }, { console: false });
        if (!data.retrying) {
            // The code and operation travel with it: only a refusal of the
            // request itself ends a client's wait (reducer.ts).
            store.dispatch({
                type: QUEUE_SERVICE_ERROR,
                error: data.message || 'Unable to request an interpreter.',
                ...typeof data.code === 'string' ? { code: data.code } : {},
                ...typeof data.operation === 'string' ? { operation: data.operation } : {}
            });
        }
    });
    console.log('[InterpreterQueue] Middleware initialized');
    return true;
}
